import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Trash2, Package, Users, Calculator, TrendingUp, Euro, FileDown, Receipt, CreditCard, ArrowUpRight, ArrowDownLeft, FileText, Clock, Pencil, Wand2, Sparkles, RefreshCw, FileSignature, Save, X, Loader2, Check, Flag, Banknote, PlusCircle, CalendarRange, ChevronDown, ChevronUp, Wallet, Eye, EyeOff } from 'lucide-react';
import { format } from 'date-fns';
import { fmtDate } from '@/lib/dateFmt';
import { toast } from 'sonner';
import { AccontiRiepilogo } from '@/components/AccontiRiepilogo';
import { CantiereSelectByCliente } from '@/components/CantiereSelectByCliente';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Tipi di conto selezionabili nell'Anteprima Conto AI
const TIPI_CONTO = [
  { id: 'totale', label: 'Conto Totale', icon: Flag, desc: 'Genera il conto finale del cantiere comprendendo tutte le lavorazioni contabilizzate.' },
  { id: 'acconto', label: 'Acconto', icon: Banknote, desc: 'Genera un conto di acconto relativo alle lavorazioni eseguite.' },
  { id: 'extra', label: 'Conto Extra', icon: PlusCircle, desc: 'Genera un conto contenente esclusivamente le lavorazioni extra approvate.' },
];

export default function ContabilitaPage() {
  const [cantieri, setCantieri] = useState([]);
  const [workers, setWorkers] = useState([]);
  // iter55: pre-selezione cantiere via ?cantiere=ID nell'URL
  const initialCantiere = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('cantiere') || '' : '';
  const [selectedCantiere, setSelectedCantiere] = useState(initialCantiere);
  const [speseMateriali, setSpeseMateriali] = useState([]);
  const [costiOperai, setCostiOperai] = useState([]);
  const [movimenti, setMovimenti] = useState([]);
  const [riepilogo, setRiepilogo] = useState(null);
  const [saldo, setSaldo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('fatturazione');
  const [cantiereRapportini, setCantiereRapportini] = useState(null);
  const [magazzinoSummary, setMagazzinoSummary] = useState(null);

  // Per-costo edit state — lets the admin override the split between internal
  // hours (cost to the company) and billable hours (price to the client).
  const [editCostoDialogOpen, setEditCostoDialogOpen] = useState(false);
  const [editingCosto, setEditingCosto] = useState(null);
  const [editCostoForm, setEditCostoForm] = useState({
    hours_costo: '', hourly_cost: '', hours_cliente: '', prezzo_cliente: '',
  });

  const [spesaDialogOpen, setSpesaDialogOpen] = useState(false);
  const [costoDialogOpen, setCostoDialogOpen] = useState(false);
  const [movDialogOpen, setMovDialogOpen] = useState(false);
  const [ricaricDialogOpen, setRicaricoDialogOpen] = useState(false);

  const [spesaForm, setSpesaForm] = useState({ ddt_number: '', fornitore: '', description: '', costo_netto: '', date: new Date().toISOString().split('T')[0] });
  const [costoForm, setCostoForm] = useState({ worker_id: '', hours: '', hourly_cost: '', date: new Date().toISOString().split('T')[0], description: '' });
  const [movForm, setMovForm] = useState({ tipo: 'fattura_emessa', importo: '', descrizione: '', data: new Date().toISOString().split('T')[0], numero_fattura: '' });
  const [ricaricoForm, setRicaricoForm] = useState({ ricarico_materiali_pct: 0, ricarico_manodopera_pct: 0, ricarico_fisso_eur: 0, ricarico_fisso_materiali_eur: 0, ricarico_fisso_manodopera_eur: 0, note: '' });

  // AI Conto state
  const [contoAiDialogOpen, setContoAiDialogOpen] = useState(false);
  const [contoAiBusy, setContoAiBusy] = useState(false);
  const [contoTemplates, setContoTemplates] = useState([]);
  const [contoAiForm, setContoAiForm] = useState({ template_id: '', output: 'docx', extra_instructions: '' });

  // AI Conto PREVIEW state (lettura inline, niente download)
  const [contoPreviewOpen, setContoPreviewOpen] = useState(false);
  const [contoPreviewBusy, setContoPreviewBusy] = useState(false);
  const [contoPreviewData, setContoPreviewData] = useState(null);
  const [contoPreviewExtra, setContoPreviewExtra] = useState('');

  // Anteprima AI inline auto-generata sopra "Impostazioni Ricarico" (iter39)
  const [inlinePreviewBusy, setInlinePreviewBusy] = useState(false);
  const [inlinePreviewData, setInlinePreviewData] = useState(null);
  const [inlinePreviewExpanded, setInlinePreviewExpanded] = useState(true);
  const [inlinePreviewExtra, setInlinePreviewExtra] = useState('');

  // Tipo di conto per l'Anteprima Conto AI (totale | acconto | extra)
  const [tipoConto, setTipoConto] = useState('');
  const [accontoNumero, setAccontoNumero] = useState('1');
  const [accontoModo, setAccontoModo] = useState('percentuale');
  const [accontoImporto, setAccontoImporto] = useState('');
  const [accontoPercentuale, setAccontoPercentuale] = useState('');

  // Conti emessi al cliente (iter39/40)
  const [contiEmessi, setContiEmessi] = useState([]);
  const [contoEmessoDialogOpen, setContoEmessoDialogOpen] = useState(false);
  const [editingContoEmesso, setEditingContoEmesso] = useState(null);
  const [contoEmessoForm, setContoEmessoForm] = useState({
    descrizione: '',
    prezzo_calcolato: 0,
    prezzo_emesso: 0,
    iva_pct: 0,  // iter40: IVA cliente
    iva_custom: false,
    stato: 'bozza',
    data_emissione: new Date().toISOString().slice(0, 10),
    note: '',
  });

  // iter78: filtro anno solare per la dashboard fatturazione del cantiere
  const currentYear = new Date().getFullYear();
  const CREDITO_TOGGLE_KEY_CANT = 'contabilita_credito_totale_visible';
  const [annoSelezionato, setAnnoSelezionato] = useState(currentYear);
  const [anniDisponibili, setAnniDisponibili] = useState([currentYear]);
  const [anniOpen, setAnniOpen] = useState(false);
  const [creditoCant, setCreditoCant] = useState(null);
  const [creditoCantVisible, setCreditoCantVisible] = useState(() => {
    try { return localStorage.getItem(CREDITO_TOGGLE_KEY_CANT) === '1'; } catch { return false; }
  });

  useEffect(() => { fetchInitialData(); }, []);
  useEffect(() => {
    if (selectedCantiere) {
      fetchCantiereData();
      // iter43: NON auto-generare più l'anteprima AI (era lenta, 5-15s).
      // L'utente clicca "Genera anteprima" quando la vuole.
      setInlinePreviewData(null);
      // iter78: reset anno all'apertura di un nuovo cantiere + carica anni disponibili
      setAnnoSelezionato(currentYear);
      axios.get(`${API}/cantieri/${selectedCantiere}/saldo/anni-disponibili`, { withCredentials: true })
        .then(r => setAnniDisponibili(r.data?.anni || [currentYear]))
        .catch(() => setAnniDisponibili([currentYear]));
    }
  }, [selectedCantiere, currentYear]);

  // iter78: ricarica il saldo quando cambia l'anno selezionato
  useEffect(() => {
    if (!selectedCantiere) return;
    axios.get(`${API}/cantieri/${selectedCantiere}/saldo`, { params: { anno: annoSelezionato }, withCredentials: true })
      .then(r => setSaldo(r.data))
      .catch(() => {});
  }, [annoSelezionato, selectedCantiere]);

  // iter78: credito cumulativo del cantiere (solo se visibile)
  useEffect(() => {
    if (!selectedCantiere || !creditoCantVisible) return;
    axios.get(`${API}/cantieri/${selectedCantiere}/saldo/credito-aperto`, { withCredentials: true })
      .then(r => setCreditoCant(r.data))
      .catch(() => setCreditoCant(null));
  }, [selectedCantiere, creditoCantVisible]);

  const toggleCreditoCant = () => {
    const next = !creditoCantVisible;
    setCreditoCantVisible(next);
    try { localStorage.setItem(CREDITO_TOGGLE_KEY_CANT, next ? '1' : '0'); } catch { /* ignore */ }
  };

  const fetchInitialData = async () => {
    try {
      const [cantieriRes, workersRes] = await Promise.all([
        axios.get(`${API}/cantieri`, { withCredentials: true }),
        axios.get(`${API}/users?role=operaio`, { withCredentials: true })
      ]);
      setCantieri(cantieriRes.data);
      setWorkers(workersRes.data);
      if (cantieriRes.data.length > 0) {} // Don't auto-select, show selection page
    } catch (error) { toast.error('Errore nel caricamento'); }
    finally { setLoading(false); }
  };

  const fetchCantiereData = async () => {
    try {
      const [speseRes, costiRes, riepilogoRes, movRes, saldoRes, rapRes, contiRes, magSummaryRes] = await Promise.all([
        axios.get(`${API}/cantieri/${selectedCantiere}/spese-materiali`, { withCredentials: true }),
        axios.get(`${API}/cantieri/${selectedCantiere}/costi-operai`, { withCredentials: true }),
        axios.get(`${API}/cantieri/${selectedCantiere}/riepilogo-costi`, { withCredentials: true }),
        axios.get(`${API}/cantieri/${selectedCantiere}/movimenti`, { withCredentials: true }),
        axios.get(`${API}/cantieri/${selectedCantiere}/saldo`, { params: { anno: annoSelezionato }, withCredentials: true }),
        axios.get(`${API}/cantieri/${selectedCantiere}/rapportini`, { withCredentials: true }),
        axios.get(`${API}/cantieri/${selectedCantiere}/conti-emessi`, { withCredentials: true }),
        axios.get(`${API}/magazzino/cantieri/${selectedCantiere}/summary`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setSpeseMateriali(speseRes.data);
      setCostiOperai(costiRes.data);
      setRiepilogo(riepilogoRes.data);
      setMovimenti(movRes.data);
      setSaldo(saldoRes.data);
      setCantiereRapportini(rapRes.data);
      setContiEmessi(contiRes.data || []);
      setMagazzinoSummary(magSummaryRes.data);
      setRicaricoForm({
        ricarico_materiali_pct: riepilogoRes.data.ricarico_materiali_pct || 0,
        ricarico_manodopera_pct: riepilogoRes.data.ricarico_manodopera_pct || 0,
        ricarico_fisso_eur: riepilogoRes.data.ricarico_fisso_eur || 0,
        ricarico_fisso_materiali_eur: riepilogoRes.data.ricarico_fisso_materiali_eur || 0,
        ricarico_fisso_manodopera_eur: riepilogoRes.data.ricarico_fisso_manodopera_eur || 0,
        note: '',
      });
    } catch (error) { console.error(error); }
  };

  const handleAddSpesa = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/cantieri/${selectedCantiere}/spese-materiali`, { ...spesaForm, cantiere_id: selectedCantiere, costo_netto: parseFloat(spesaForm.costo_netto) }, { withCredentials: true });
      toast.success('Spesa aggiunta');
      setSpesaDialogOpen(false);
      setSpesaForm({ ddt_number: '', fornitore: '', description: '', costo_netto: '', date: new Date().toISOString().split('T')[0] });
      fetchCantiereData();
    } catch (error) { toast.error('Errore'); }
  };

  const handleAddCosto = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/cantieri/${selectedCantiere}/costi-operai`, { ...costoForm, cantiere_id: selectedCantiere, hours: parseFloat(costoForm.hours), hourly_cost: parseFloat(costoForm.hourly_cost) }, { withCredentials: true });
      toast.success('Costo aggiunto');
      setCostoDialogOpen(false);
      setCostoForm({ worker_id: '', hours: '', hourly_cost: '', date: new Date().toISOString().split('T')[0], description: '' });
      fetchCantiereData();
    } catch (error) { toast.error('Errore'); }
  };

  const handleAddMovimento = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/cantieri/${selectedCantiere}/movimenti`, { ...movForm, cantiere_id: selectedCantiere, importo: parseFloat(movForm.importo) }, { withCredentials: true });
      toast.success('Movimento registrato');
      setMovDialogOpen(false);
      setMovForm({ tipo: 'fattura_emessa', importo: '', descrizione: '', data: new Date().toISOString().split('T')[0], numero_fattura: '' });
      fetchCantiereData();
    } catch (error) { toast.error('Errore'); }
  };

  const handleSaveRicarico = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/cantieri/${selectedCantiere}/conto-cliente`, {
        cantiere_id: selectedCantiere,
        ricarico_materiali_pct: parseItNum(ricaricoForm.ricarico_materiali_pct),
        ricarico_manodopera_pct: parseItNum(ricaricoForm.ricarico_manodopera_pct),
        // iter63: FORZATAMENTE 0 per disattivare la retrocompatibilità legacy
        // (il valore legacy `ricarico_fisso_eur` veniva mantenuto anche quando i
        // due nuovi campi erano azzerati, impedendo di riportare a 0 il fisso).
        ricarico_fisso_eur: 0,
        ricarico_fisso_materiali_eur: parseItNum(ricaricoForm.ricarico_fisso_materiali_eur),
        ricarico_fisso_manodopera_eur: parseItNum(ricaricoForm.ricarico_fisso_manodopera_eur),
        note: ricaricoForm.note,
      }, { withCredentials: true });
      toast.success('Ricarico salvato — totale aggiornato');
      setRicaricoDialogOpen(false);
      fetchCantiereData();
    } catch (error) { toast.error(error?.response?.data?.detail || 'Errore salvataggio ricarico'); }
  };

  const handleDeleteSpesa = async (id) => { try { await axios.delete(`${API}/spese-materiali/${id}`, { withCredentials: true }); toast.success('Eliminata'); fetchCantiereData(); } catch { toast.error('Errore'); } };
  const handleDeleteCosto = async (id) => { try { await axios.delete(`${API}/costi-operai/${id}`, { withCredentials: true }); toast.success('Eliminato'); fetchCantiereData(); } catch { toast.error('Errore'); } };

  const openEditCosto = (c) => {
    setEditingCosto(c);
    // Fallback ladder for the client-side rate:
    //   1. value already on the costo (admin override or sync-default)
    //   2. `prezzo_cliente` configured on the worker's anagrafica
    //   3. internal hourly_cost (better than a blank field)
    const worker = workers.find(w => w.id === c.worker_id);
    const anagraficaPrezzo = worker?.prezzo_cliente;
    const anagraficaCosto = worker?.hourly_cost;
    const resolvedPrezzo = c.prezzo_cliente && c.prezzo_cliente > 0
      ? c.prezzo_cliente
      : (anagraficaPrezzo || c.hourly_cost || '');
    const resolvedCosto = c.hourly_cost && c.hourly_cost > 0
      ? c.hourly_cost
      : (anagraficaCosto || '');
    setEditCostoForm({
      hours_costo: String(c.hours_costo ?? c.hours ?? ''),
      hourly_cost: String(resolvedCosto),
      hours_cliente: String(c.hours_cliente ?? c.hours ?? ''),
      prezzo_cliente: String(resolvedPrezzo),
    });
    setEditCostoDialogOpen(true);
  };

  const handleUpdateCosto = async (e) => {
    e.preventDefault();
    if (!editingCosto) return;
    try {
      const payload = {
        hours_costo: parseFloat(editCostoForm.hours_costo) || 0,
        hourly_cost: parseFloat(editCostoForm.hourly_cost) || 0,
        hours_cliente: parseFloat(editCostoForm.hours_cliente) || 0,
        prezzo_cliente: parseFloat(editCostoForm.prezzo_cliente) || 0,
      };
      await axios.put(`${API}/costi-operai/${editingCosto.id}`, payload, { withCredentials: true });
      toast.success('Ore aggiornate');
      setEditCostoDialogOpen(false);
      setEditingCosto(null);
      fetchCantiereData();
    } catch {
      toast.error('Errore aggiornamento');
    }
  };
  const handleDeleteMovimento = async (id) => { try { await axios.delete(`${API}/movimenti/${id}`, { withCredentials: true }); toast.success('Eliminato'); fetchCantiereData(); } catch { toast.error('Errore'); } };

  const selectWorker = (workerId) => {
    const worker = workers.find(w => w.id === workerId);
    setCostoForm({ ...costoForm, worker_id: workerId, hourly_cost: worker?.hourly_cost || '' });
  };

  const handleDownloadPdf = async () => {
    if (!selectedCantiere) return;
    try {
      const response = await axios.get(`${API}/cantieri/${selectedCantiere}/report-pdf`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const cantiere = cantieri.find(c => c.id === selectedCantiere);
      link.download = `report_${cantiere?.name?.replace(/\s+/g, '_') || 'cantiere'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report PDF scaricato');
    } catch { toast.error('Errore nel download'); }
  };

  // Apri dialog Conto AI: precarica i template disponibili (conteggio + generico)
  const openContoAiDialog = async () => {
    setContoAiDialogOpen(true);
    setContoAiForm({ template_id: '', output: 'docx', extra_instructions: '' });
    try {
      const r = await axios.get(`${API}/document-templates/list`, { withCredentials: true });
      const all = r.data || [];
      // Mostra solo i template adatti a un conto cantiere
      const relevant = all.filter(t => ['conteggio', 'generico', 'fattura', 'preventivo'].includes(t.doc_type));
      setContoTemplates(relevant);
    } catch {
      setContoTemplates([]);
    }
  };

  const handleGenerateContoAi = async () => {
    if (!selectedCantiere) return;
    setContoAiBusy(true);
    try {
      const cantiere = cantieri.find(c => c.id === selectedCantiere);
      const payload = {
        template_id: contoAiForm.template_id || null,
        output: contoAiForm.output,
        extra_instructions: contoAiForm.extra_instructions || '',
      };
      const r = await axios.post(`${API}/cantieri/${selectedCantiere}/conto-ai`, payload, {
        withCredentials: true,
        responseType: 'blob',
      });
      const ext = contoAiForm.output === 'pdf' ? 'pdf' : 'docx';
      const mime = ext === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      const url = window.URL.createObjectURL(new Blob([r.data], { type: mime }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `conto_${(cantiere?.name || 'cantiere').replace(/\s+/g, '_')}.${ext}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Conto AI generato e scaricato');
      setContoAiDialogOpen(false);
    } catch (err) {
      let msg = 'Errore generazione conto AI';
      // Blob → try to read as text
      if (err?.response?.data instanceof Blob) {
        try { msg = JSON.parse(await err.response.data.text()).detail || msg; } catch { /* ignore */ }
      } else if (err?.response?.data?.detail) {
        msg = err.response.data.detail;
      }
      toast.error(msg);
    } finally {
      setContoAiBusy(false);
    }
  };

  // Anteprima AI inline: nessun download, solo lettura in dashboard
  const openContoPreview = async () => {
    if (!selectedCantiere) return;
    setContoPreviewOpen(true);
    setContoPreviewData(null);
    await fetchContoPreview('');
  };

  const fetchContoPreview = async (extra) => {
    if (!selectedCantiere) return;
    setContoPreviewBusy(true);
    try {
      const r = await axios.post(
        `${API}/cantieri/${selectedCantiere}/conto-ai-preview`,
        { extra_instructions: extra || '' },
        { withCredentials: true, timeout: 90000 },
      );
      setContoPreviewData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore generazione anteprima AI');
      setContoPreviewData(null);
    } finally {
      setContoPreviewBusy(false);
    }
  };

  // Anteprima AI INLINE: auto-generata al cambio cantiere, niente popup
  const buildTipoContoPayload = () => {
    if (!tipoConto) return {};
    const p = { tipo_conto: tipoConto };
    if (tipoConto === 'acconto') {
      p.acconto_numero = accontoNumero || '';
      const imp = parseItNum(accontoImporto);
      const pct = parseItNum(accontoPercentuale);
      if (accontoModo === 'importo' && imp > 0) p.acconto_importo = imp;
      else if (accontoModo === 'percentuale' && pct > 0) p.acconto_percentuale = pct;
    }
    return p;
  };

  const fetchInlinePreview = async (extra = '') => {
    if (!selectedCantiere) return;
    setInlinePreviewBusy(true);
    try {
      const r = await axios.post(
        `${API}/cantieri/${selectedCantiere}/conto-ai-preview`,
        { extra_instructions: extra || '', ...buildTipoContoPayload() },
        { withCredentials: true, timeout: 120000 },
      );
      setInlinePreviewData(r.data);
    } catch (err) {
      // Silenzioso: l'anteprima è un "extra", non bloccare la pagina
      console.warn('Errore anteprima inline', err);
      setInlinePreviewData(null);
    } finally {
      setInlinePreviewBusy(false);
    }
  };

  // Conti emessi al cliente (iter39)
  const openNewContoEmesso = () => {
    setEditingContoEmesso(null);
    // iter40: il "totale calcolato" pre-popolato è ora il TOTALE COERENTE coi due box
    // (somma materiali al cliente + manodopera al cliente). L'utente sceglie poi l'IVA.
    const totale = riepilogo?.totale_cliente_con_iva || riepilogo?.totale_cliente || 0;
    setContoEmessoForm({
      descrizione: `Conto cantiere ${cantiereCorrente?.name || ''}`.trim(),
      prezzo_calcolato: totale,
      prezzo_emesso: Math.round(totale),  // arrotondamento commerciale di default
      iva_pct: 0,
      iva_custom: false,
      stato: 'bozza',
      data_emissione: new Date().toISOString().slice(0, 10),
      note: '',
    });
    setContoEmessoDialogOpen(true);
  };

  // iter101: deep-link dal widget "Promemoria Conti" della dashboard.
  // Se l'URL contiene ?crea_conto=1, apre automaticamente il tab "Conti emessi"
  // e il dialog di creazione una volta caricato il riepilogo del cantiere.
  const creaContoTriggered = useRef(false);
  useEffect(() => {
    if (creaContoTriggered.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('crea_conto') === '1' && selectedCantiere && riepilogo) {
      creaContoTriggered.current = true;
      setActiveTab('conti-emessi');
      setTimeout(() => openNewContoEmesso(), 200);
    }
  }, [selectedCantiere, riepilogo]); // eslint-disable-line react-hooks/exhaustive-deps

  const openEditContoEmesso = (c) => {
    setEditingContoEmesso(c);
    const ivaPct = c.iva_pct || 0;
    const isCustom = ![0, 4, 10, 22].includes(ivaPct);
    setContoEmessoForm({
      descrizione: c.descrizione || '',
      prezzo_calcolato: c.prezzo_calcolato || 0,
      prezzo_emesso: c.prezzo_emesso || 0,
      iva_pct: ivaPct,
      iva_custom: isCustom,
      stato: c.stato || 'bozza',
      data_emissione: c.data_emissione || new Date().toISOString().slice(0, 10),
      note: c.note || '',
    });
    setContoEmessoDialogOpen(true);
  };

  // iter58: helper per parse numero italiano (accetta sia "16376,39" che "16376.39")
  const parseItNum = (val) => {
    if (typeof val === 'number') return val;
    if (!val) return 0;
    let s = String(val).trim();
    if (s.includes(',')) {
      // Formato italiano: punti = migliaia, virgola = decimali
      s = s.replace(/\./g, '').replace(',', '.');
    } else {
      const dots = (s.match(/\./g) || []).length;
      // Un solo punto seguito da 1-2 cifre finali → separatore decimale (es. "46872.09")
      if (!(dots === 1 && /\.\d{1,2}$/.test(s))) {
        s = s.replace(/\./g, '');
      }
    }
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : 0;
  };

  const handleSaveContoEmesso = async (e) => {
    if (e) e.preventDefault();
    if (!selectedCantiere) return;
    try {
      const payload = {
        descrizione: contoEmessoForm.descrizione,
        prezzo_calcolato: parseItNum(contoEmessoForm.prezzo_calcolato),
        prezzo_emesso: parseItNum(contoEmessoForm.prezzo_emesso),
        iva_pct: parseItNum(contoEmessoForm.iva_pct),
        stato: contoEmessoForm.stato,
        data_emissione: contoEmessoForm.data_emissione,
        note: contoEmessoForm.note,
        riepilogo_snapshot: editingContoEmesso ? undefined : (riepilogo || {}),
      };
      if (editingContoEmesso) {
        await axios.put(
          `${API}/cantieri/${selectedCantiere}/conti-emessi/${editingContoEmesso.id}`,
          payload, { withCredentials: true },
        );
        toast.success('Conto aggiornato');
      } else {
        await axios.post(
          `${API}/cantieri/${selectedCantiere}/conti-emessi`,
          payload, { withCredentials: true },
        );
        toast.success('Conto emesso creato');
      }
      setContoEmessoDialogOpen(false);
      fetchCantiereData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore salvataggio conto');
    }
  };

  const handleDeleteContoEmesso = async (id) => {
    if (!window.confirm('Eliminare questo conto emesso?')) return;
    try {
      await axios.delete(`${API}/cantieri/${selectedCantiere}/conti-emessi/${id}`, { withCredentials: true });
      toast.success('Conto eliminato');
      fetchCantiereData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const handleChangeStatoContoEmesso = async (id, newStato) => {
    try {
      await axios.put(
        `${API}/cantieri/${selectedCantiere}/conti-emessi/${id}`,
        { stato: newStato }, { withCredentials: true },
      );
      setContiEmessi(prev => prev.map(c => c.id === id ? { ...c, stato: newStato } : c));
    } catch {
      toast.error('Errore aggiornamento stato');
    }
  };

  // iter42: trasforma Conto Emesso → Fattura Attiva
  const [convertConfirmOpen, setConvertConfirmOpen] = useState(false);
  const [contoToConvert, setContoToConvert] = useState(null);
  const [convertingBusy, setConvertingBusy] = useState(false);

  const openConvertDialog = (c) => {
    if (c.fattura_uscita_id) {
      toast.info(`Già trasformata nella fattura ${c.fattura_uscita_numero}`);
      return;
    }
    setContoToConvert(c);
    setConvertConfirmOpen(true);
  };

  const handleConvertToFattura = async () => {
    if (!contoToConvert) return;
    setConvertingBusy(true);
    try {
      const r = await axios.post(
        `${API}/cantieri/${selectedCantiere}/conti-emessi/${contoToConvert.id}/converti-in-fattura`,
        {},  // tutti i default lato backend
        { withCredentials: true },
      );
      toast.success(`Fattura ${r.data.fattura?.numero || ''} creata`);
      setConvertConfirmOpen(false);
      setContoToConvert(null);
      fetchCantiereData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore conversione');
    } finally {
      setConvertingBusy(false);
    }
  };


  const tipoLabels = { fattura_emessa: 'Fattura Emessa', pagamento_ricevuto: 'Pagamento Ricevuto', costo_extra: 'Costo Extra' };
  const tipoColors = { fattura_emessa: 'text-blue-700 bg-blue-50 border-blue-200', pagamento_ricevuto: 'text-emerald-700 bg-emerald-50 border-emerald-200', costo_extra: 'text-red-700 bg-red-50 border-red-200' };
  const statoLabels = { in_attesa: 'In Attesa', pagata: 'Pagata', scaduta: 'Scaduta', completato: '' };
  const statoColors = { in_attesa: 'bg-amber-100 text-amber-700 border-amber-200', pagata: 'bg-emerald-100 text-emerald-700 border-emerald-200', scaduta: 'bg-red-100 text-red-700 border-red-200' };

  // Color mapping for movimento amount based on tipo (avoids nested ternary in JSX)
  const movimentoImportoColors = {
    pagamento_ricevuto: 'text-emerald-700',
    costo_extra: 'text-red-700',
    fattura_emessa: 'text-blue-700',
  };
  const getMovimentoImportoClass = (tipo) => movimentoImportoColors[tipo] || 'text-blue-700';
  const getMovimentoImportoPrefix = (tipo) => (tipo === 'pagamento_ricevuto' ? '+' : '');

  const handleChangeStato = async (movId, newStato) => {
    try {
      await axios.put(`${API}/movimenti/${movId}/stato?stato=${newStato}`, {}, { withCredentials: true });
      toast.success(`Stato aggiornato: ${statoLabels[newStato]}`);
      fetchCantiereData();
    } catch { toast.error('Errore'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;

  // Landing page: cantiere selection
  if (!selectedCantiere) {
    const cantieriAttivi = cantieri.filter(c => c.status === 'attivo');
    const cantieriAltri = cantieri.filter(c => c.status !== 'attivo');
    return (
      <div className="space-y-6" data-testid="contabilita-page">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Contabilità</h1>
          <p className="text-slate-500 mt-1">Seleziona il cantiere di cui visualizzare la contabilità</p>
        </div>
        {cantieriAttivi.length > 0 && (
          <>
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">Cantieri Attivi</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {cantieriAttivi.map(c => (
                <Card key={c.id} className="border-slate-200 hover:border-slate-400 hover:shadow-md transition-all cursor-pointer group" onClick={() => setSelectedCantiere(c.id)} data-testid={`select-cantiere-${c.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-900 group-hover:text-blue-700 transition-colors truncate">{c.name}</h3>
                        {c.client_name && <p className="text-sm text-slate-500 mt-0.5">{c.client_name}</p>}
                        {c.address && <p className="text-xs text-slate-400 mt-1 truncate">{c.address}</p>}
                      </div>
                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 ml-2 shrink-0" variant="outline">attivo</Badge>
                    </div>
                    {c.prezzo_preventivato > 0 && (
                      <p className="text-xs text-slate-500 mt-3 border-t border-slate-100 pt-2">Preventivato: <span className="font-semibold text-slate-700">{c.prezzo_preventivato.toLocaleString('it-IT', {minimumFractionDigits: 2})} &euro;</span></p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
        {cantieriAltri.length > 0 && (
          <>
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mt-4">Altri Cantieri</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {cantieriAltri.map(c => (
                <Card key={c.id} className="border-slate-100 hover:border-slate-300 hover:shadow transition-all cursor-pointer opacity-75 hover:opacity-100" onClick={() => setSelectedCantiere(c.id)}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-slate-700 truncate">{c.name}</h3>
                        {c.client_name && <p className="text-sm text-slate-400 mt-0.5">{c.client_name}</p>}
                      </div>
                      <Badge variant="outline" className="text-xs ml-2 shrink-0">{c.status}</Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
        {cantieri.length === 0 && (
          <Card className="border-slate-200"><CardContent className="py-12 text-center text-slate-500">Nessun cantiere trovato. Crea un cantiere per iniziare.</CardContent></Card>
        )}
      </div>
    );
  }

  const cantiereCorrente = cantieri.find(c => c.id === selectedCantiere);
  const totaleLavori = saldo?.ricavi_totali || 0;

  return (
    <div className="space-y-6" data-testid="contabilita-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-slate-500 -ml-2" onClick={() => setSelectedCantiere('')} data-testid="back-to-cantieri-btn">
              &larr; Tutti i cantieri
            </Button>
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">{cantiereCorrente?.name || 'Contabilità'}</h1>
          <p className="text-slate-500 mt-1">{cantiereCorrente?.client_name || 'Fatturazione, costi e saldo'}</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button onClick={openContoPreview} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="conto-preview-btn">
            <Sparkles className="h-4 w-4 mr-2" />Anteprima AI
          </Button>
          <Button onClick={openContoAiDialog} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="conto-ai-btn">
            <Wand2 className="h-4 w-4 mr-2" />Scarica Conto AI
          </Button>
          <Button variant="outline" onClick={handleDownloadPdf} data-testid="download-pdf-btn">
            <FileDown className="h-4 w-4 mr-2" />Report PDF
          </Button>
          <div className="w-72">
            <CantiereSelectByCliente
              cantieri={cantieri}
              value={selectedCantiere}
              onChange={setSelectedCantiere}
              placeholder="Seleziona cantiere"
              allowNone={false}
              dataTestId="select-cantiere"
            />
          </div>
        </div>
      </div>

      {/* Report Instantaneo */}
          {saldo && (
            <div className="space-y-4">
              {/* SEZIONE MATERIALI */}
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">Materiali</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-xs text-blue-600">1. Ricavi Materiali</p>
                      <p className="text-sm text-slate-400">Costi DDT + Ricarico {saldo.ricarico_materiali_pct}%</p>
                      <p className="text-xl font-bold text-blue-700">{(saldo.ricavi_materiali || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} &euro;</p>
                    </div>
                    <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                      <p className="text-xs text-red-600">2. Costo Materiali</p>
                      <p className="text-sm text-slate-500">Netto (imponibile)</p>
                      <p className="text-lg font-bold text-red-700">{(saldo.costo_materiali_netto || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} &euro;</p>
                      <p className="text-[11px] text-slate-500 mt-1">+ IVA {saldo.iva_materiali_pct}% (passthrough): <span className="font-medium">{(saldo.iva_materiali || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} &euro;</span></p>
                      <p className="text-[10px] text-slate-400">Lordo: {(saldo.costo_materiali_totale || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} &euro;</p>
                    </div>
                    <div className={`p-3 rounded-lg border ${(saldo.utile_materiali || 0) >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                      <p className="text-xs text-slate-600">3. {(saldo.utile_materiali || 0) >= 0 ? 'Utile' : 'Perdita'} Materiali</p>
                      <p className={`text-xl font-bold ${(saldo.utile_materiali || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{(saldo.utile_materiali || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} &euro;</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* SEZIONE OPERAI */}
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">Manodopera</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-xs text-blue-600">4. Ricavi Operai</p>
                      <p className="text-sm text-slate-400">Ore x Prezzo Cliente</p>
                      <p className="text-xl font-bold text-blue-700">{(saldo.ricavi_operai || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} &euro;</p>
                    </div>
                    <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                      <p className="text-xs text-red-600">5. Costo Operai</p>
                      <p className="text-sm text-slate-400">Ore x Costo Orario</p>
                      <p className="text-xl font-bold text-red-700">{(saldo.costo_operai || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} &euro;</p>
                    </div>
                    <div className={`p-3 rounded-lg border ${(saldo.utile_operai || 0) >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                      <p className="text-xs text-slate-600">6. {(saldo.utile_operai || 0) >= 0 ? 'Utile' : 'Perdita'} Manodopera</p>
                      <p className={`text-xl font-bold ${(saldo.utile_operai || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{(saldo.utile_operai || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} &euro;</p>
                    </div>
                  </div>
                  {saldo.operai_detail && saldo.operai_detail.length > 0 && (
                    <div className="mt-3 border-t border-slate-100 pt-3">
                      <p className="text-xs text-slate-500 mb-2">Dettaglio per operaio:</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                        {saldo.operai_detail.map((op) => (
                          <div key={op.name} className="flex items-center justify-between text-sm px-2 py-1 rounded bg-slate-50">
                            <span className="font-medium">{op.name}</span>
                            <span className="text-slate-500">{op.hours}h &middot; Costo {op.costo.toLocaleString('it-IT', {minimumFractionDigits: 2})} &middot; Ricavo {op.ricavo.toLocaleString('it-IT', {minimumFractionDigits: 2})}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* TOTALI */}
              <Card className="border-slate-200 border-2">
                <CardContent className="p-4">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">Riepilogo Totale</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="p-3 bg-blue-100 rounded-lg border border-blue-300">
                      <p className="text-xs text-blue-700 font-semibold">7. Totale Ricavi</p>
                      <p className="text-sm text-slate-500">Materiali + Operai</p>
                      <p className="text-2xl font-bold text-blue-800">{(saldo.ricavi_totali || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} &euro;</p>
                    </div>
                    <div className="p-3 bg-red-100 rounded-lg border border-red-300">
                      <p className="text-xs text-red-700 font-semibold">8. Totale Spese</p>
                      <p className="text-sm text-slate-500">Materiali (+ IVA) + Operai</p>
                      <p className="text-2xl font-bold text-red-800">{(saldo.costi_totali || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} &euro;</p>
                    </div>
                    <div className={`p-3 rounded-lg border-2 ${(saldo.utile_totale || 0) >= 0 ? 'bg-emerald-100 border-emerald-400' : 'bg-red-100 border-red-400'}`}>
                      <p className={`text-xs font-semibold ${(saldo.utile_totale || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>9. {(saldo.utile_totale || 0) >= 0 ? 'UTILE TOTALE' : 'PERDITA TOTALE'}</p>
                      <p className={`text-2xl font-bold ${(saldo.utile_totale || 0) >= 0 ? 'text-emerald-800' : 'text-red-800'}`}>{(saldo.utile_totale || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} &euro;</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* iter78: Header con selettore anno + toggle "Credito totale aperto" */}
              <div className="flex items-center justify-between gap-3 flex-wrap pt-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Receipt className="h-4 w-4 text-slate-500 shrink-0" />
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
                    {annoSelezionato === 0 ? (
                      <>Fatturazione — <span className="text-indigo-700">Totale storico</span></>
                    ) : (
                      <>Fatturazione — anno <span className="text-slate-900">{annoSelezionato}</span>
                      {annoSelezionato !== currentYear && <span className="ml-1.5 text-amber-600 normal-case tracking-normal text-xs">(anno passato)</span>}</>
                    )}
                  </h3>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    variant={creditoCantVisible ? 'default' : 'outline'}
                    size="sm"
                    onClick={toggleCreditoCant}
                    className={creditoCantVisible ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : ''}
                    data-testid="toggle-credito-cantiere"
                    title="Mostra/nascondi il credito totale aperto del cantiere cumulativo su tutti gli anni"
                  >
                    {creditoCantVisible ? <EyeOff className="h-4 w-4 mr-1.5" /> : <Eye className="h-4 w-4 mr-1.5" />}
                    Credito totale
                  </Button>
                  <div className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1" data-testid="cantiere-year-selector">
                    <CalendarRange className="h-4 w-4 text-slate-500" />
                    <Select value={String(annoSelezionato)} onValueChange={(v) => setAnnoSelezionato(parseInt(v, 10))}>
                      <SelectTrigger className="h-8 w-[140px] border-0 bg-transparent px-1 focus:ring-0" data-testid="cantiere-year-trigger">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0" data-testid="cantiere-year-option-totale">
                          Totale storico
                        </SelectItem>
                        {(anniDisponibili || []).map(a => (
                          <SelectItem key={a} value={String(a)} data-testid={`cantiere-year-option-${a}`}>
                            {a}{a === currentYear ? ' (corrente)' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {annoSelezionato !== currentYear && (
                      <button
                        type="button"
                        onClick={() => setAnnoSelezionato(currentYear)}
                        className="ml-1 text-[11px] text-blue-600 hover:text-blue-800 underline whitespace-nowrap"
                        data-testid="cantiere-reset-year-btn"
                      >
                        Torna a {currentYear}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* iter78: Casella "Credito totale aperto" del cantiere — cumulativa su tutti gli anni */}
              {creditoCantVisible && (
                <Card className="border-indigo-300 bg-gradient-to-r from-indigo-50 to-blue-50" data-testid="cantiere-credito-totale">
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-indigo-100 rounded-lg">
                          <Wallet className="h-4 w-4 text-indigo-700" />
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-indigo-700 font-semibold">Credito aperto — cumulativo su tutti gli anni</p>
                          <p className="text-xl font-bold text-indigo-900" data-testid="cantiere-credito-value">
                            {creditoCant ? (creditoCant.totale || 0).toLocaleString('it-IT', {minimumFractionDigits: 2}) : '…'} <span className="text-sm font-normal">€</span>
                          </p>
                        </div>
                      </div>
                      {creditoCant?.per_anno?.length > 0 && (
                        <div className="text-xs text-indigo-800 min-w-[220px]">
                          <p className="font-semibold mb-0.5">Per anno:</p>
                          <div className="space-y-0.5 max-h-20 overflow-y-auto">
                            {creditoCant.per_anno.map(r => (
                              <div key={r.anno} className="flex justify-between gap-3" data-testid={`cantiere-credito-anno-${r.anno}`}>
                                <span>{r.anno || 'n/d'}</span>
                                <span className="font-mono font-semibold">{(r.aperto || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} €</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Fatturato row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="border-slate-200 border-l-4 border-l-blue-500"><CardContent className="p-4"><p className="text-xs uppercase tracking-wider text-slate-500">Fatturato</p><p className="text-xl font-bold text-blue-700">{(saldo.fatturato || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})}</p><p className="text-xs text-slate-400">{saldo.num_fatture} fatture · {saldo.show_total ? 'totale storico' : annoSelezionato}</p></CardContent></Card>
                <Card className="border-slate-200 border-l-4 border-l-emerald-500"><CardContent className="p-4"><p className="text-xs uppercase tracking-wider text-slate-500">Incassato</p><p className="text-xl font-bold text-emerald-700">{(saldo.incassato || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})}</p><p className="text-xs text-slate-400">{saldo.num_pagamenti} pagamenti · {saldo.show_total ? 'totale storico' : annoSelezionato}</p></CardContent></Card>
                <Card className="border-slate-200 border-l-4 border-l-amber-500"><CardContent className="p-4"><p className="text-xs uppercase tracking-wider text-slate-500">Da Incassare</p><p className={`text-xl font-bold ${(saldo.da_incassare || 0) > 0 ? 'text-amber-700' : 'text-slate-400'}`}>{(saldo.da_incassare || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})}</p><p className="text-xs text-slate-400">{saldo.show_total ? 'totale storico' : annoSelezionato}</p></CardContent></Card>
                {saldo.prezzo_preventivato > 0 && <Card className="border-slate-200 border-l-4 border-l-purple-500"><CardContent className="p-4"><p className="text-xs uppercase tracking-wider text-slate-500">Preventivato</p><p className="text-xl font-bold text-purple-700">{(saldo.prezzo_preventivato || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})}</p></CardContent></Card>}
              </div>

              {/* iter86-i/j: Utile REALE — confronto Fatturato vs Costi effettivi azienda */}
              <Card className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50/60 to-white" data-testid="utile-reale-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-indigo-600" />
                    Utile reale cantiere ({saldo.show_total ? 'TOTALE STORICO' : annoSelezionato})
                  </CardTitle>
                  <p className="text-xs text-slate-500 mt-1">
                    Confronto tra quanto <strong>hai fatturato</strong> al cliente e quanto ti è <strong>effettivamente costato</strong> (materiali netti + costi operai) — è il vero utile del cantiere.
                    {!saldo.show_total && <span className="block text-amber-600 mt-1">⚠️ Stai visualizzando solo l&apos;anno <strong>{annoSelezionato}</strong>. Per cantieri pluriennali seleziona <strong>&quot;Totale storico&quot;</strong> nel selettore anno per vedere l&apos;utile reale complessivo.</span>}
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                      <p className="text-[10px] uppercase text-blue-600 font-semibold">Fatturato cliente</p>
                      <p className="text-xl font-bold text-blue-800" data-testid="ur-fatturato">{(saldo.fatturato || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} €</p>
                      <p className="text-[10px] text-slate-500 mt-0.5">{saldo.num_fatture} fatture emesse</p>
                    </div>
                    <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                      <p className="text-[10px] uppercase text-red-600 font-semibold">Costi azienda</p>
                      <p className="text-xl font-bold text-red-800" data-testid="ur-costi">{(saldo.costi_totali || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} €</p>
                      <p className="text-[10px] text-slate-500 mt-0.5">Materiali {(saldo.costo_materiali_netto || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} + Operai {(saldo.costo_operai || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})}</p>
                    </div>
                    <div className={`p-3 rounded-lg border-2 ${(saldo.utile_reale || 0) >= 0 ? 'bg-emerald-50 border-emerald-400' : 'bg-red-100 border-red-500'}`}>
                      <p className={`text-[10px] uppercase font-semibold ${(saldo.utile_reale || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                        {(saldo.utile_reale || 0) >= 0 ? 'UTILE REALE' : 'PERDITA'}
                      </p>
                      <p className={`text-2xl font-bold ${(saldo.utile_reale || 0) >= 0 ? 'text-emerald-800' : 'text-red-800'}`} data-testid="ur-utile">{(saldo.utile_reale || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} €</p>
                      <p className={`text-[10px] mt-0.5 ${(saldo.utile_reale || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>Fatturato − Costi</p>
                    </div>
                    <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                      <p className="text-[10px] uppercase text-slate-500 font-semibold">Margine %</p>
                      <p className={`text-2xl font-bold ${(saldo.margine_reale_pct || 0) >= 15 ? 'text-emerald-700' : (saldo.margine_reale_pct || 0) >= 0 ? 'text-amber-700' : 'text-red-700'}`} data-testid="ur-margine">
                        {(saldo.margine_reale_pct || 0).toFixed(1)}%
                      </p>
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        {(saldo.margine_reale_pct || 0) >= 20 ? 'Ottimo margine' : (saldo.margine_reale_pct || 0) >= 10 ? 'Margine sano' : (saldo.margine_reale_pct || 0) >= 0 ? 'Margine sottile' : 'In perdita'}
                      </p>
                    </div>
                  </div>

                  {/* iter86-l: Utile Reale affiancato — SOLO PREVENTIVATO vs TOTALE (include Extra) */}
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg border border-purple-300 bg-purple-50/60" data-testid="utile-reale-preventivato">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-[10px] uppercase text-purple-700 font-semibold tracking-wide">Utile reale — solo preventivato</p>
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-200 text-purple-800 font-semibold uppercase">acconti</span>
                      </div>
                      <div className="flex items-baseline justify-between">
                        <p className={`text-xl font-bold ${(saldo?.preventivato?.utile_reale || 0) >= 0 ? 'text-purple-800' : 'text-red-700'}`} data-testid="utile-preventivato-value">
                          {(saldo?.preventivato?.utile_reale || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} €
                        </p>
                        <p className={`text-sm font-semibold ${(saldo?.preventivato?.margine_reale_pct || 0) >= 15 ? 'text-emerald-700' : (saldo?.preventivato?.margine_reale_pct || 0) >= 0 ? 'text-amber-700' : 'text-red-700'}`}>
                          {(saldo?.preventivato?.margine_reale_pct || 0).toFixed(1)}%
                        </p>
                      </div>
                      <p className="text-[10px] text-slate-500 mt-1">
                        Fatt. <strong>{(saldo?.preventivato?.fatturato || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} €</strong> − Costi <strong>{(saldo?.preventivato?.costi_totale || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} €</strong>
                      </p>
                    </div>
                    <div className="p-3 rounded-lg border border-indigo-300 bg-indigo-50/60" data-testid="utile-reale-totale">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-[10px] uppercase text-indigo-700 font-semibold tracking-wide">Utile reale — totale (con Extra)</p>
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-indigo-200 text-indigo-800 font-semibold uppercase">tutto</span>
                      </div>
                      <div className="flex items-baseline justify-between">
                        <p className={`text-xl font-bold ${(saldo.utile_reale || 0) >= 0 ? 'text-indigo-800' : 'text-red-700'}`} data-testid="utile-totale-value">
                          {(saldo.utile_reale || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} €
                        </p>
                        <p className={`text-sm font-semibold ${(saldo.margine_reale_pct || 0) >= 15 ? 'text-emerald-700' : (saldo.margine_reale_pct || 0) >= 0 ? 'text-amber-700' : 'text-red-700'}`}>
                          {(saldo.margine_reale_pct || 0).toFixed(1)}%
                        </p>
                      </div>
                      <p className="text-[10px] text-slate-500 mt-1">
                        Fatt. <strong>{(saldo.fatturato || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} €</strong> − Costi <strong>{(saldo.costi_totali || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} €</strong>
                      </p>
                    </div>
                  </div>

                  {saldo.prezzo_preventivato > 0 && (
                    <div className="mt-4 p-3 rounded-lg bg-purple-50 border border-purple-200">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                          <p className="text-xs text-purple-700 font-semibold uppercase">Confronto vs Preventivato</p>
                          <p className="text-[11px] text-slate-600 mt-0.5">Preventivato: <strong>{(saldo.prezzo_preventivato || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} €</strong> · Fatturato preventivato: <strong>{(saldo?.preventivato?.fatturato || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} €</strong></p>
                          <p className="text-[10px] text-slate-400">Gli Extra sono esclusi dal confronto (sono nella card sotto).</p>
                        </div>
                        <div className="text-right">
                          <p className={`text-lg font-bold ${(saldo?.preventivato?.differenza_vs_preventivato || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`} data-testid="ur-vs-preventivo">
                            {(saldo?.preventivato?.differenza_vs_preventivato || 0) >= 0 ? '+' : ''}{(saldo?.preventivato?.differenza_vs_preventivato || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} €
                          </p>
                          <p className="text-[10px] text-slate-500">
                            {(saldo?.preventivato?.differenza_vs_preventivato || 0) >= 0 ? 'oltre il preventivo (senza contare Extra)' : 'sotto il preventivo (mancato ricavo)'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* iter86-l: Card Extra (fuori preventivo) — contabilità separata */}
              {((saldo?.extra?.fatturato || 0) !== 0 || (saldo?.extra?.costi_totale || 0) !== 0) && (
                <Card className="border-2 border-amber-300 bg-gradient-to-br from-amber-50/70 to-white" data-testid="extra-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-amber-600" />
                      Extra (fuori preventivo) — {saldo.show_total ? 'TOTALE STORICO' : annoSelezionato}
                    </CardTitle>
                    <p className="text-xs text-slate-500 mt-1">
                      Contabilità separata per fatture emesse/ricevute e ore operai marcate come <strong>Extra</strong> (lavori aggiuntivi non inclusi nel preventivo). Serve a capire quanto extra hai generato senza intaccare il confronto con il preventivato.
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                        <p className="text-[10px] uppercase text-blue-600 font-semibold">Fatturato Extra</p>
                        <p className="text-xl font-bold text-blue-800" data-testid="extra-fatturato">{(saldo?.extra?.fatturato || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} €</p>
                        <p className="text-[10px] text-slate-500 mt-0.5">{saldo?.extra?.num_fatture_emesse || 0} fatture emesse</p>
                      </div>
                      <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                        <p className="text-[10px] uppercase text-red-600 font-semibold">Costi Extra</p>
                        <p className="text-xl font-bold text-red-800" data-testid="extra-costi">{(saldo?.extra?.costi_totale || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} €</p>
                        <p className="text-[10px] text-slate-500 mt-0.5">
                          Fornitori {(saldo?.extra?.costi_fatture || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} + Operai {(saldo?.extra?.costo_operai || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})}
                        </p>
                      </div>
                      <div className={`p-3 rounded-lg border-2 ${(saldo?.extra?.utile || 0) >= 0 ? 'bg-emerald-50 border-emerald-400' : 'bg-red-100 border-red-500'}`}>
                        <p className={`text-[10px] uppercase font-semibold ${(saldo?.extra?.utile || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                          {(saldo?.extra?.utile || 0) >= 0 ? 'UTILE EXTRA' : 'PERDITA EXTRA'}
                        </p>
                        <p className={`text-2xl font-bold ${(saldo?.extra?.utile || 0) >= 0 ? 'text-emerald-800' : 'text-red-800'}`} data-testid="extra-utile">{(saldo?.extra?.utile || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} €</p>
                        <p className={`text-[10px] mt-0.5 ${(saldo?.extra?.utile || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>Fatt. Extra − Costi Extra</p>
                      </div>
                      <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                        <p className="text-[10px] uppercase text-slate-500 font-semibold">Margine Extra %</p>
                        <p className={`text-2xl font-bold ${(saldo?.extra?.margine_pct || 0) >= 15 ? 'text-emerald-700' : (saldo?.extra?.margine_pct || 0) >= 0 ? 'text-amber-700' : 'text-red-700'}`} data-testid="extra-margine">
                          {(saldo?.extra?.margine_pct || 0).toFixed(1)}%
                        </p>
                        <p className="text-[10px] text-slate-400 mt-0.5">{saldo?.extra?.num_fatture_ricevute || 0} fatt. ricevute Extra</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* iter78: Sezione "Anni precedenti di fatturazione" collassata di default */}
              {(anniDisponibili || []).filter(a => a !== annoSelezionato && a < currentYear).length > 0 && (
                <Card className="border-slate-200" data-testid="cantiere-anni-precedenti">
                  <CardHeader className="pb-2 cursor-pointer select-none" onClick={() => setAnniOpen(!anniOpen)} data-testid="cantiere-anni-toggle">
                    <CardTitle className="text-sm font-semibold flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <CalendarRange className="h-4 w-4 text-slate-500" />
                        Anni precedenti di fatturazione del cantiere
                        <span className="text-xs font-normal text-slate-500">
                          ({(anniDisponibili || []).filter(a => a !== annoSelezionato && a < currentYear).length} disponibili)
                        </span>
                      </span>
                      {anniOpen ? <ChevronUp className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
                    </CardTitle>
                  </CardHeader>
                  {anniOpen && (
                    <CardContent>
                      <p className="text-xs text-slate-500 mb-3">Clicca un anno per vedere i dati di fatturazione di quel periodo. Il credito totale aperto resta cumulativo.</p>
                      <div className="flex flex-wrap gap-2">
                        {(anniDisponibili || []).filter(a => a !== annoSelezionato && a < currentYear).sort((a,b) => b - a).map(a => (
                          <button
                            key={a}
                            type="button"
                            onClick={() => { setAnnoSelezionato(a); setAnniOpen(false); }}
                            className="px-3 py-1.5 rounded-md border border-slate-200 bg-white hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 text-sm font-medium transition-colors"
                            data-testid={`cantiere-anno-passato-${a}`}
                          >
                            {a}
                          </button>
                        ))}
                      </div>
                    </CardContent>
                  )}
                </Card>
              )}
            </div>
          )}

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <TabsList>
                <TabsTrigger value="fatturazione" data-testid="tab-fatturazione"><Receipt className="h-4 w-4 mr-1.5" />Fatturazione</TabsTrigger>
                <TabsTrigger value="conti-emessi" data-testid="tab-conti-emessi"><FileSignature className="h-4 w-4 mr-1.5" />Conti emessi{contiEmessi.length ? ` (${contiEmessi.length})` : ''}</TabsTrigger>
                <TabsTrigger value="acconti" data-testid="tab-acconti"><Euro className="h-4 w-4 mr-1.5" />Acconti</TabsTrigger>
                <TabsTrigger value="materiali" data-testid="tab-materiali"><Package className="h-4 w-4 mr-1.5" />Spese DDT</TabsTrigger>
                <TabsTrigger value="operai" data-testid="tab-operai"><Users className="h-4 w-4 mr-1.5" />Costi Operai</TabsTrigger>
                <TabsTrigger value="rapportini" data-testid="tab-rapportini"><FileText className="h-4 w-4 mr-1.5" />Rapportini{cantiereRapportini?.summary?.total_rapportini ? ` (${cantiereRapportini.summary.total_rapportini})` : ''}</TabsTrigger>
              </TabsList>
              <div className="flex gap-2">
                {activeTab === 'fatturazione' && (
                  <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={() => setMovDialogOpen(true)} data-testid="add-movimento-btn">
                    <Plus className="h-4 w-4 mr-1.5" />Nuovo Movimento
                  </Button>
                )}
                {activeTab === 'conti-emessi' && (
                  <Button onClick={openNewContoEmesso} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-conto-emesso-btn">
                    <Plus className="h-4 w-4 mr-1.5" />Genera conto
                  </Button>
                )}
                {activeTab === 'materiali' && (
                  <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={() => setSpesaDialogOpen(true)} data-testid="add-spesa-btn">
                    <Plus className="h-4 w-4 mr-1.5" />Aggiungi Spesa DDT
                  </Button>
                )}
                {activeTab === 'operai' && (
                  <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={() => setCostoDialogOpen(true)} data-testid="add-costo-btn">
                    <Plus className="h-4 w-4 mr-1.5" />Aggiungi Costo Operaio
                  </Button>
                )}
              </div>
            </div>

            {/* Acconti Tab (iter61) */}
            <TabsContent value="acconti">
              <AccontiRiepilogo cantiereId={selectedCantiere} onContoCreato={fetchCantiereData} />
            </TabsContent>

            {/* Fatturazione Tab */}
            <TabsContent value="fatturazione">
              <Card className="border-slate-200">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Data</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Tipo</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">N. Fattura</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Descrizione</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Stato</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Importo</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 w-12"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {movimenti.length === 0 ? (
                        <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500"><Receipt className="h-8 w-8 mx-auto text-slate-300 mb-2" />Nessun movimento registrato. Aggiungi fatture e pagamenti.</TableCell></TableRow>
                      ) : movimenti.map((m) => (
                        <TableRow key={m.id} className="border-slate-100">
                          <TableCell className="text-slate-600">{fmtDate(m.data, 'dd-MM-yyyy')}</TableCell>
                          <TableCell><Badge className={tipoColors[m.tipo]} variant="outline">{m.tipo === 'fattura_emessa' && <ArrowUpRight className="h-3 w-3 mr-1" />}{m.tipo === 'pagamento_ricevuto' && <ArrowDownLeft className="h-3 w-3 mr-1" />}{tipoLabels[m.tipo]}</Badge></TableCell>
                          <TableCell className="font-mono text-sm text-slate-600">{m.numero_fattura || '-'}</TableCell>
                          <TableCell className="text-slate-600 max-w-xs truncate">{m.descrizione}</TableCell>
                          <TableCell>
                            {m.tipo === 'fattura_emessa' ? (
                              <select
                                value={m.stato || 'in_attesa'}
                                onChange={(e) => handleChangeStato(m.id, e.target.value)}
                                className={`text-xs font-medium px-2 py-1 rounded-full border cursor-pointer ${statoColors[m.stato || 'in_attesa']}`}
                                data-testid={`stato-select-${m.id}`}
                              >
                                <option value="in_attesa">In Attesa</option>
                                <option value="pagata">Pagata</option>
                                <option value="scaduta">Scaduta</option>
                              </select>
                            ) : (
                              <span className="text-xs text-slate-400">{m.stato === 'completato' ? '' : m.stato || ''}</span>
                            )}
                          </TableCell>
                          <TableCell className={`text-right font-medium ${getMovimentoImportoClass(m.tipo)}`}>{getMovimentoImportoPrefix(m.tipo)}{m.importo.toLocaleString('it-IT', {minimumFractionDigits: 2})}</TableCell>
                          <TableCell><Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => handleDeleteMovimento(m.id)}><Trash2 className="h-4 w-4" /></Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Conti Emessi Tab (iter39) */}
            <TabsContent value="conti-emessi">
              <Card className="border-slate-200">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">N.</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Data</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Descrizione</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Imponibile</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">IVA</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Totale Cliente</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Stato</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 w-32"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {contiEmessi.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-10 text-slate-500">
                            <FileSignature className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                            <p className="text-sm">Nessun conto ancora emesso per questo cantiere.</p>
                            <p className="text-xs text-slate-400 mt-1">Clicca "Genera conto" per crearne uno partendo dal totale calcolato.</p>
                          </TableCell>
                        </TableRow>
                      ) : contiEmessi.map((c) => {
                        const statoColorClass = {
                          bozza: 'bg-slate-100 text-slate-700 border-slate-200',
                          inviato: 'bg-blue-100 text-blue-700 border-blue-200',
                          pagato: 'bg-emerald-100 text-emerald-700 border-emerald-200',
                          annullato: 'bg-red-100 text-red-700 border-red-200',
                        }[c.stato] || 'bg-slate-100 text-slate-700 border-slate-200';
                        const ivaPct = c.iva_pct || 0;
                        const ivaEur = c.iva_eur != null ? c.iva_eur : (c.prezzo_emesso || 0) * ivaPct / 100;
                        const totaleLordo = c.totale_lordo != null ? c.totale_lordo : (c.prezzo_emesso || 0) + ivaEur;
                        return (
                          <TableRow key={c.id} data-testid={`conto-emesso-row-${c.id}`}>
                            <TableCell className="font-mono text-xs text-slate-700">{c.numero}</TableCell>
                            <TableCell className="text-slate-600 text-sm">
                              {fmtDate(c.data_emissione, 'dd-MM-yyyy')}
                            </TableCell>
                            <TableCell className="text-sm max-w-xs truncate">{c.descrizione}</TableCell>
                            <TableCell className="text-right font-mono text-sm text-slate-700">
                              {(c.prezzo_emesso || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} €
                            </TableCell>
                            <TableCell className="text-right text-xs">
                              <span className="text-slate-500">{ivaPct}%</span>
                              <span className="block font-mono text-slate-600">{ivaEur.toLocaleString('it-IT', {minimumFractionDigits: 2})} €</span>
                            </TableCell>
                            <TableCell className="text-right font-bold font-mono text-emerald-700">
                              {totaleLordo.toLocaleString('it-IT', {minimumFractionDigits: 2})} €
                            </TableCell>
                            <TableCell>
                              <select
                                value={c.stato || 'bozza'}
                                onChange={(e) => handleChangeStatoContoEmesso(c.id, e.target.value)}
                                className={`text-xs font-medium px-2 py-1 rounded-full border cursor-pointer ${statoColorClass}`}
                                data-testid={`conto-emesso-stato-${c.id}`}
                              >
                                <option value="bozza">Bozza</option>
                                <option value="inviato">Inviato</option>
                                <option value="pagato">Pagato</option>
                                <option value="annullato">Annullato</option>
                              </select>
                            </TableCell>
                            <TableCell className="text-right whitespace-nowrap">
                              {c.fattura_uscita_id ? (
                                <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-300 mr-1" title={`Fattura ${c.fattura_uscita_numero}`}>
                                  <Receipt className="h-3 w-3 mr-0.5" />{c.fattura_uscita_numero}
                                </Badge>
                              ) : (
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-600 hover:bg-emerald-50" onClick={() => openConvertDialog(c)} title="Trasforma in fattura attiva" data-testid={`conto-emesso-convert-${c.id}`}>
                                  <ArrowUpRight className="h-3.5 w-3.5" />
                                </Button>
                              )}
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditContoEmesso(c)} data-testid={`conto-emesso-edit-${c.id}`}>
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => handleDeleteContoEmesso(c.id)} data-testid={`conto-emesso-delete-${c.id}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
              {/* Suggerimento riepilogo */}
              {riepilogo && (
                <div className="mt-3 text-xs text-slate-500 bg-slate-50 border rounded-lg p-3">
                  <span className="font-semibold text-slate-700">Totale calcolato attuale</span> (dal pannello "Impostazioni Ricarico"): <span className="font-mono">{(riepilogo.totale_cliente_con_iva || riepilogo.totale_cliente || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} €</span> · Puoi creare un nuovo conto emesso e arrotondare manualmente questo importo prima dell'invio al cliente.
                </div>
              )}
            </TabsContent>

            {/* Materiali Tab */}
            <TabsContent value="materiali" className="space-y-3">
              {/* Card riepilogo prelievi da Magazzino (iter100d) */}
              {magazzinoSummary && magazzinoSummary.num_prelievi > 0 && (
                <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white" data-testid="magazzino-summary-card">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2 text-purple-700 font-semibold text-sm">
                          <Package className="h-4 w-4" />
                          Prelievi da Magazzino
                        </div>
                        <div className="text-2xl font-bold text-slate-900 mt-1">€ {(magazzinoSummary.costo_totale_magazzino || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{magazzinoSummary.num_prelievi} prelievi · {magazzinoSummary.num_materiali_distinti} materiali distinti</div>
                        <div className="text-[11px] text-emerald-700 mt-1 flex items-center gap-1">
                          <Check className="h-3 w-3" />
                          Già incluso nel totale materiali del riepilogo cantiere (righe "Bozza · Magazzino" qui sotto)
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`/admin/magazzino?cantiere=${selectedCantiere}`, '_blank')}
                        data-testid="magazzino-summary-view-all"
                      >
                        Vedi storico completo →
                      </Button>
                    </div>
                    {magazzinoSummary.breakdown && magazzinoSummary.breakdown.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-purple-100">
                        <div className="text-[11px] text-slate-500 uppercase tracking-wider mb-2">Top materiali</div>
                        <div className="space-y-1">
                          {magazzinoSummary.breakdown.slice(0, 5).map(b => (
                            <div key={b.materiale_id} className="flex items-center justify-between text-xs">
                              <span className="text-slate-700 truncate max-w-[60%]">{b.materiale_nome}</span>
                              <span className="text-slate-500">
                                {b.quantita_totale} pz · <b className="text-slate-800">€ {(b.costo_totale || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</b>
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
              <Card className="border-slate-200">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">N. DDT</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Data</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Fornitore</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Descrizione</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Costo Netto</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 w-12"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {speseMateriali.length === 0 ? (
                        <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">Nessuna spesa</TableCell></TableRow>
                      ) : speseMateriali.map((s) => {
                        const fromMag = s.source === 'magazzino';
                        return (
                        <TableRow key={s.id} className={`border-slate-100 ${fromMag ? 'bg-purple-50/40' : ''}`} data-testid={`spesa-row-${s.id}`}>
                          <TableCell className="font-mono font-medium text-slate-900">
                            {s.ddt_number}
                            {fromMag && <Badge variant="outline" className="ml-2 text-[10px] text-purple-700 border-purple-200 bg-purple-50">Bozza · Magazzino</Badge>}
                          </TableCell>
                          <TableCell className="text-slate-600">{fmtDate(s.date, 'dd-MM-yyyy')}</TableCell>
                          <TableCell className="text-slate-900">{s.fornitore}</TableCell>
                          <TableCell className="text-slate-600 max-w-xs truncate">{s.description}</TableCell>
                          <TableCell className="text-right font-medium text-slate-900">{s.costo_netto.toLocaleString('it-IT', {minimumFractionDigits: 2})}</TableCell>
                          <TableCell>
                            {fromMag ? (
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 cursor-not-allowed" title="Bozza generata dal Magazzino. Per eliminarla, rimuovi il movimento nello Storico Magazzino." disabled>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            ) : (
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => handleDeleteSpesa(s.id)}><Trash2 className="h-4 w-4" /></Button>
                            )}
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Operai Tab */}
            <TabsContent value="operai">
              <Card className="border-slate-200">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Operaio</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Data</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 border-r border-slate-200" colSpan={3}>
                          <div className="flex items-center gap-1.5"><span>Costo società</span></div>
                        </TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500" colSpan={3}>
                          <div className="flex items-center gap-1.5"><span>Prezzo cliente</span></div>
                        </TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 w-20"></TableHead>
                      </TableRow>
                      <TableRow className="bg-slate-50 border-t border-slate-100">
                        <TableHead className="text-[10px] uppercase text-slate-400"></TableHead>
                        <TableHead className="text-[10px] uppercase text-slate-400"></TableHead>
                        <TableHead className="text-[10px] uppercase text-slate-400 text-right">Ore</TableHead>
                        <TableHead className="text-[10px] uppercase text-slate-400 text-right">€/h</TableHead>
                        <TableHead className="text-[10px] uppercase text-slate-400 text-right border-r border-slate-200">Totale</TableHead>
                        <TableHead className="text-[10px] uppercase text-slate-400 text-right">Ore</TableHead>
                        <TableHead className="text-[10px] uppercase text-slate-400 text-right">€/h</TableHead>
                        <TableHead className="text-[10px] uppercase text-slate-400 text-right">Totale</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {costiOperai.length === 0 ? (
                        <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-500">Nessun costo operaio</TableCell></TableRow>
                      ) : costiOperai.map((c) => {
                        const hCosto = c.hours_costo ?? c.hours ?? 0;
                        const hCliente = c.hours_cliente;
                        const pCliente = c.prezzo_cliente;
                        const tCliente = c.total_cliente;
                        const edited = c.manually_edited;
                        return (
                          <TableRow key={c.id} className={`border-slate-100 ${edited ? 'bg-blue-50/30' : ''}`}>
                            <TableCell className="font-medium text-slate-900">
                              {c.worker_name}
                              {edited && <Badge variant="outline" className="ml-2 bg-blue-50 text-blue-700 border-blue-200 text-[10px]">modificato</Badge>}
                            </TableCell>
                            <TableCell className="text-slate-600 text-sm">{fmtDate(c.date, 'dd-MM-yyyy')}</TableCell>
                            {/* Costo società */}
                            <TableCell className="text-right text-slate-900">{hCosto}h</TableCell>
                            <TableCell className="text-right text-slate-600">{(c.hourly_cost || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})}</TableCell>
                            <TableCell className="text-right font-medium text-slate-900 border-r border-slate-200">{(c.total || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})}</TableCell>
                            {/* Prezzo cliente */}
                            <TableCell className="text-right text-emerald-700">
                              {hCliente != null ? `${hCliente}h` : <span className="text-slate-300">—</span>}
                            </TableCell>
                            <TableCell className="text-right text-emerald-700">
                              {pCliente != null ? (pCliente).toLocaleString('it-IT', {minimumFractionDigits: 2}) : <span className="text-slate-300">—</span>}
                            </TableCell>
                            <TableCell className="text-right font-medium text-emerald-700">
                              {tCliente != null ? tCliente.toLocaleString('it-IT', {minimumFractionDigits: 2}) : <span className="text-slate-300">—</span>}
                            </TableCell>
                            <TableCell className="text-right whitespace-nowrap">
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600" onClick={() => openEditCosto(c)} title="Modifica ore" data-testid={`edit-costo-${c.id}`}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => handleDeleteCosto(c.id)} title="Elimina">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Rapportini Tab */}
            <TabsContent value="rapportini">
              <Card className="border-slate-200">
                <CardContent className="p-0">
                  {cantiereRapportini?.summary && (
                    <div className="p-4 border-b border-slate-100 bg-slate-50/50">
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                        <div className="p-3 bg-white rounded-lg border"><p className="text-slate-500 text-xs">Rapportini</p><p className="font-bold text-lg">{cantiereRapportini.summary.total_rapportini}</p></div>
                        <div className="p-3 bg-white rounded-lg border"><p className="text-slate-500 text-xs">Ore Totali</p><p className="font-bold text-lg flex items-center gap-1"><Clock className="h-4 w-4 text-blue-500" />{cantiereRapportini.summary.total_hours}h</p></div>
                        <div className="p-3 bg-white rounded-lg border"><p className="text-slate-500 text-xs">Straordinari</p><p className="font-bold text-lg text-amber-600">{cantiereRapportini.summary.total_straordinari}h</p></div>
                        <div className="p-3 bg-white rounded-lg border"><p className="text-slate-500 text-xs">Giornate/Uomo</p><p className="font-bold text-lg">{cantiereRapportini.summary.total_workers_days}</p></div>
                        <div className="p-3 bg-white rounded-lg border"><p className="text-slate-500 text-xs">Trasferte</p><p className="font-bold text-lg">{cantiereRapportini.summary.trasferte}</p></div>
                      </div>
                      {Object.keys(cantiereRapportini.summary.by_worker || {}).length > 0 && (
                        <div className="mt-4">
                          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Riepilogo per Operaio</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                            {Object.entries(cantiereRapportini.summary.by_worker).map(([name, data]) => (
                              <div key={name} className="flex items-center justify-between p-2 bg-white rounded border text-sm">
                                <span className="font-medium text-slate-700 truncate mr-2">{name}</span>
                                <span className="text-slate-500 whitespace-nowrap">{data.days}gg &middot; {data.hours}h {data.straordinari > 0 ? <span className="text-amber-600">(+{data.straordinari}h str.)</span> : ''}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs uppercase w-24">Data</TableHead>
                        <TableHead className="text-xs uppercase w-28">Orario</TableHead>
                        <TableHead className="text-xs uppercase">Operai</TableHead>
                        <TableHead className="text-xs uppercase min-w-[300px]">Lavorazioni svolte</TableHead>
                        <TableHead className="text-xs uppercase text-right w-20">Ore</TableHead>
                        <TableHead className="text-xs uppercase text-right w-20">Str.</TableHead>
                        <TableHead className="text-xs uppercase text-center w-24">Allegati</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(!cantiereRapportini?.rapportini || cantiereRapportini.rapportini.length === 0) ? (
                        <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">Nessun rapportino per questo cantiere</TableCell></TableRow>
                      ) : (
                        cantiereRapportini.rapportini.map(r => {
                          // Resolve workers list (workers_list takes precedence, then operai_tecnici fallback)
                          const workersList = (r.workers_list && r.workers_list.length > 0)
                            ? r.workers_list.map(w => w.name).filter(Boolean).join(', ')
                            : (r.operai_tecnici || r.worker_name || '-');
                          const orario = (r.ora_inizio && r.ora_fine) ? `${r.ora_inizio}–${r.ora_fine}` : '-';
                          const fotoCount = Array.isArray(r.photos) ? r.photos.length : 0;
                          const hasSignature = !!r.signature;
                          return (
                            <TableRow key={r.id} className="border-slate-100 align-top">
                              <TableCell className="text-sm whitespace-nowrap pt-3">
                                {r.date?.split('-').reverse().join('-')}
                                {r.trasferta && <div className="mt-0.5"><Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 text-[10px]">Trasferta</Badge></div>}
                                {r.numero && <div className="mt-0.5 text-[10px] text-slate-400">#{r.numero}</div>}
                              </TableCell>
                              <TableCell className="text-xs text-slate-600 pt-3 whitespace-nowrap font-mono">{orario}</TableCell>
                              <TableCell className="text-xs text-slate-700 pt-3"><div className="font-medium whitespace-pre-line leading-snug">{workersList}</div></TableCell>
                              <TableCell className="text-sm text-slate-700 pt-3">
                                <div className="whitespace-pre-wrap break-words leading-relaxed">{r.description || <span className="text-slate-400 italic">Nessuna descrizione</span>}</div>
                              </TableCell>
                              <TableCell className="text-sm font-medium text-right pt-3">{r.hours_worked}h</TableCell>
                              <TableCell className="text-sm text-right pt-3">{r.ore_straordinarie > 0 ? <span className="text-amber-600 font-medium">{r.ore_straordinarie}h</span> : <span className="text-slate-300">-</span>}</TableCell>
                              <TableCell className="text-xs pt-3 text-center">
                                <div className="flex items-center justify-center gap-1.5">
                                  {fotoCount > 0 && (
                                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[10px]" title={`${fotoCount} foto`}>
                                      📷 {fotoCount}
                                    </Badge>
                                  )}
                                  {hasSignature && (
                                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]" title="Firmato">✓</Badge>
                                  )}
                                  {fotoCount === 0 && !hasSignature && <span className="text-slate-300">-</span>}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* ANTEPRIMA AI INLINE (sopra Impostazioni Ricarico, iter39) */}
          <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50/40 to-white" data-testid="inline-preview-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-indigo-600" />
                  Anteprima Conto AI
                  {inlinePreviewBusy && <Loader2 className="h-4 w-4 animate-spin text-indigo-400" />}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setInlinePreviewExpanded(v => !v)} data-testid="inline-preview-toggle">
                    {inlinePreviewExpanded ? 'Comprimi' : 'Espandi'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => fetchInlinePreview(inlinePreviewExtra)} disabled={inlinePreviewBusy || !tipoConto} title={!tipoConto ? 'Seleziona prima un tipo di conto' : ''} data-testid="inline-preview-refresh">
                    <Sparkles className={`h-3.5 w-3.5 mr-1.5 ${inlinePreviewBusy ? 'animate-pulse' : ''}`} />Genera Conto AI
                  </Button>
                </div>
              </div>
            </CardHeader>
            {inlinePreviewExpanded && (
              <CardContent className="space-y-4">
                {/* Tipo di conto (iter69) */}
                <div className="space-y-3" data-testid="tipo-conto-section">
                  {totaleLavori > 0 && (
                    <p className="text-sm text-slate-600" data-testid="totale-lavori-label">
                      Totale lavori contabilizzato: <span className="font-bold text-slate-900">{totaleLavori.toLocaleString('it-IT', { minimumFractionDigits: 2 })} €</span>
                    </p>
                  )}
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-700">Tipo di conto</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {TIPI_CONTO.map(t => {
                      const TipoIcon = t.icon;
                      const isSelected = tipoConto === t.id;
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => setTipoConto(t.id)}
                          className={`relative text-left p-4 rounded-xl border-2 transition-colors duration-150 ${isSelected ? 'border-blue-500 bg-blue-50/70 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'}`}
                          data-testid={`tipo-conto-${t.id}`}
                        >
                          {isSelected && (
                            <span className="absolute top-2 right-2 flex items-center justify-center h-5 w-5 rounded-full bg-blue-600">
                              <Check className="h-3 w-3 text-white" />
                            </span>
                          )}
                          <TipoIcon className={`h-6 w-6 mb-2 ${isSelected ? 'text-blue-600' : 'text-slate-400'}`} />
                          <p className={`text-sm font-semibold ${isSelected ? 'text-blue-900' : 'text-slate-800'}`}>{t.label}</p>
                          <p className="text-xs text-slate-500 mt-1">{t.desc}</p>
                        </button>
                      );
                    })}
                  </div>
                  {tipoConto === 'acconto' && (
                    <div className="p-4 bg-white border border-blue-200 rounded-xl space-y-3" data-testid="acconto-fields">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Numero acconto</Label>
                          <Input type="number" min="1" value={accontoNumero} onChange={e => setAccontoNumero(e.target.value)} data-testid="acconto-numero-input" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Modalità</Label>
                          <div className="flex rounded-md border border-slate-200 overflow-hidden h-10">
                            <button type="button" onClick={() => setAccontoModo('importo')} className={`flex-1 text-sm font-medium transition-colors ${accontoModo === 'importo' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-testid="acconto-modo-importo">Importo €</button>
                            <button type="button" onClick={() => setAccontoModo('percentuale')} className={`flex-1 text-sm font-medium transition-colors ${accontoModo === 'percentuale' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-testid="acconto-modo-percentuale">Percentuale %</button>
                          </div>
                        </div>
                        <div className="space-y-1">
                          {accontoModo === 'importo' ? (
                            <>
                              <Label className="text-xs">Importo (€)</Label>
                              <Input value={accontoImporto} onChange={e => setAccontoImporto(e.target.value)} placeholder="es. 10.000,00" data-testid="acconto-importo-input" />
                            </>
                          ) : (
                            <>
                              <Label className="text-xs">Percentuale (%)</Label>
                              <Input value={accontoPercentuale} onChange={e => setAccontoPercentuale(e.target.value)} placeholder="es. 30" data-testid="acconto-percentuale-input" />
                            </>
                          )}
                        </div>
                      </div>
                      {accontoModo === 'percentuale' && parseItNum(accontoPercentuale) > 0 && totaleLavori > 0 && (
                        <p className="text-sm text-slate-600" data-testid="acconto-importo-live">
                          Importo acconto: <span className="font-bold text-blue-700">{(totaleLavori * parseItNum(accontoPercentuale) / 100).toLocaleString('it-IT', { minimumFractionDigits: 2 })} €</span>
                        </p>
                      )}
                    </div>
                  )}
                </div>
                {inlinePreviewBusy && !inlinePreviewData && (
                  <div className="text-center py-6 text-slate-500 text-sm" data-testid="inline-preview-loading">
                    <Sparkles className="h-8 w-8 mx-auto animate-pulse text-indigo-400 mb-2" />
                    L'AI sta componendo il riassunto del cantiere…
                  </div>
                )}
                {!inlinePreviewBusy && !inlinePreviewData && (
                  <div className="text-center py-6 space-y-3" data-testid="inline-preview-empty">
                    <Sparkles className="h-10 w-10 mx-auto text-indigo-300" />
                    <div>
                      <p className="text-sm text-slate-600">{tipoConto ? 'Genera il conto AI del cantiere' : 'Seleziona il tipo di conto qui sopra per iniziare'}</p>
                      <p className="text-[11px] text-slate-400">Aggrega rapportini, materiali e costi in un testo professionale. Richiede 5-15 secondi.</p>
                    </div>
                    <Button onClick={() => fetchInlinePreview('')} disabled={!tipoConto} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="inline-preview-generate">
                      <Sparkles className="h-4 w-4 mr-2" />
                      Genera Conto AI
                    </Button>
                  </div>
                )}
                {inlinePreviewData && (
                  <div className="space-y-3" data-testid="inline-preview-content">
                    {inlinePreviewData.oggetto && (
                      <div className="bg-white border border-indigo-200 rounded-lg p-3">
                        <p className="text-[10px] uppercase tracking-wider font-semibold text-indigo-600 mb-1">Oggetto</p>
                        <p className="font-semibold text-slate-900 text-base">{inlinePreviewData.oggetto}</p>
                      </div>
                    )}
                    {inlinePreviewData.testo_intro && (
                      <p className="text-sm text-slate-700 italic border-l-4 border-indigo-200 pl-3">{inlinePreviewData.testo_intro}</p>
                    )}
                    {Array.isArray(inlinePreviewData.lavorazioni_punti) && inlinePreviewData.lavorazioni_punti.length > 0 && (
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wide text-slate-700 mb-1.5">Lavorazioni svolte</p>
                        <ol className="list-decimal list-outside ml-5 space-y-1 text-sm text-slate-800" data-testid="inline-preview-list">
                          {inlinePreviewData.lavorazioni_punti.map((p, i) => (
                            <li key={`inline-punto-${i}`} className="pl-1">{p}</li>
                          ))}
                        </ol>
                      </div>
                    )}
                    {inlinePreviewData.testo_chiusura && (
                      <p className="text-sm text-slate-700 italic border-l-4 border-emerald-200 pl-3">{inlinePreviewData.testo_chiusura}</p>
                    )}
                    <div className="flex items-center gap-2 pt-2 border-t">
                      <Input
                        placeholder="Affina con istruzioni (opzionale, es: tono più formale)…"
                        value={inlinePreviewExtra}
                        onChange={(e) => setInlinePreviewExtra(e.target.value)}
                        className="text-xs"
                        data-testid="inline-preview-extra"
                      />
                      <Button size="sm" variant="outline" onClick={() => fetchInlinePreview(inlinePreviewExtra)} disabled={inlinePreviewBusy}>
                        Applica
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            )}
          </Card>

          {/* Impostazioni Ricarico */}
          {riepilogo && (
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2"><Calculator className="h-5 w-5 text-blue-600" />Impostazioni Ricarico</CardTitle>
                  <Button variant="outline" size="sm" onClick={() => setRicaricoDialogOpen(true)} data-testid="edit-ricarico-btn"><Calculator className="h-4 w-4 mr-1.5" />Modifica %</Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div className="p-3 bg-slate-50 rounded-lg border">
                    <p className="text-slate-500 text-xs">Ricarico Materiali %</p>
                    <p className="text-lg font-bold text-slate-900">{riepilogo.ricarico_materiali_pct}%</p>
                    <p className="text-xs text-blue-600">+{(riepilogo.ricarico_materiali_eur || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} &euro;</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg border">
                    <p className="text-slate-500 text-xs">Ricarico Manodopera %</p>
                    <p className="text-lg font-bold text-slate-900">{riepilogo.ricarico_manodopera_pct}%</p>
                    <p className="text-xs text-blue-600">+{(riepilogo.ricarico_manodopera_eur || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} &euro;</p>
                  </div>
                  <div className="p-3 bg-indigo-50/40 rounded-lg border border-indigo-100">
                    <p className="text-slate-500 text-xs">Fisso Materiali</p>
                    <p className="text-lg font-bold text-indigo-700">{(riepilogo.ricarico_fisso_materiali_eur || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} &euro;</p>
                  </div>
                  <div className="p-3 bg-amber-50/40 rounded-lg border border-amber-100">
                    <p className="text-slate-500 text-xs">Fisso Manodopera</p>
                    <p className="text-lg font-bold text-amber-700">{(riepilogo.ricarico_fisso_manodopera_eur || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} &euro;</p>
                  </div>
                </div>

                {/* Breakdown costo al cliente (iter37) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="p-4 bg-indigo-50/50 rounded-lg border border-indigo-200 space-y-1.5" data-testid="breakdown-materiali-cliente">
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-indigo-700">Materiali al cliente</p>
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-600">Costo netto</span>
                      <span className="font-mono text-slate-800">{(riepilogo.totale_materiali || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} &euro;</span>
                    </div>
                    {(riepilogo.iva_materiali_eur || 0) > 0 && (
                      <div className="flex justify-between text-[10px] text-slate-400 italic">
                        <span>(IVA 22% pagata al fornitore: incluso nel costo)</span>
                        <span className="font-mono">{(riepilogo.iva_materiali_eur || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} &euro;</span>
                      </div>
                    )}
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-600">+ Ricarico {riepilogo.ricarico_materiali_pct || 0}%</span>
                      <span className="font-mono text-slate-800">{(riepilogo.ricarico_materiali_eur || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} &euro;</span>
                    </div>
                    {(riepilogo.ricarico_fisso_materiali_eur || 0) > 0 && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-600">+ Fisso materiali</span>
                        <span className="font-mono text-indigo-700">{(riepilogo.ricarico_fisso_materiali_eur || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} &euro;</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center pt-1.5 border-t border-indigo-200">
                      <span className="text-sm font-semibold text-indigo-900">Totale</span>
                      <span className="text-lg font-bold text-indigo-700 font-mono" data-testid="materiali-al-cliente">
                        {(riepilogo.materiali_al_cliente_eur || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} &euro;
                      </span>
                    </div>
                  </div>

                  <div className="p-4 bg-amber-50/60 rounded-lg border border-amber-200 space-y-1.5" data-testid="breakdown-manodopera-cliente">
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-amber-700">Manodopera al cliente</p>
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-600">Costo interno</span>
                      <span className="font-mono text-slate-800">{(riepilogo.totale_manodopera || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} &euro;</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-600">Prezzo cliente concordato</span>
                      <span className="font-mono text-slate-800">{(riepilogo.totale_manodopera_cliente || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} &euro;</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-600">Margine manodopera</span>
                      <span className="font-mono text-emerald-700">+{((riepilogo.totale_manodopera_cliente || 0) - (riepilogo.totale_manodopera || 0)).toLocaleString('it-IT', {minimumFractionDigits: 2})} &euro;</span>
                    </div>
                    {(riepilogo.ricarico_fisso_manodopera_eur || 0) > 0 && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-600">+ Fisso manodopera</span>
                        <span className="font-mono text-amber-700">{(riepilogo.ricarico_fisso_manodopera_eur || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} &euro;</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center pt-1.5 border-t border-amber-200">
                      <span className="text-sm font-semibold text-amber-900">Totale</span>
                      <span className="text-lg font-bold text-amber-700 font-mono" data-testid="manodopera-al-cliente">
                        {(riepilogo.manodopera_al_cliente_eur || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} &euro;
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-xs text-blue-600">Totale Cliente</p>
                    <p className="text-xl font-bold text-blue-700" data-testid="totale-cliente-value">{(riepilogo.totale_cliente || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} &euro;</p>
                    <p className="text-xs text-slate-400">Costi base (IVA inclusa) + ricarichi · IVA cliente da scegliere all'emissione</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg border">
                    <p className="text-xs text-slate-500">Totale Costi Base</p>
                    <p className="text-xl font-bold text-slate-700">{(riepilogo.totale_costi || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} &euro;</p>
                    <p className="text-xs text-slate-400">Mat. lordo {(riepilogo.totale_materiali_lordo || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} + Op. {(riepilogo.totale_manodopera || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})}</p>
                    {(riepilogo.iva_materiali_eur || 0) > 0 && (
                      <p className="text-[10px] text-slate-400 mt-0.5 italic">
                        (incluso IVA forn. {(riepilogo.iva_materiali_eur || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} €)
                      </p>
                    )}
                  </div>
                  <div className={`p-3 rounded-lg border ${(riepilogo.margine || 0) >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                    <p className={`text-xs ${(riepilogo.margine || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{(riepilogo.margine || 0) >= 0 ? 'Margine' : 'Perdita'}</p>
                    <p className={`text-xl font-bold ${(riepilogo.margine || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{(riepilogo.margine || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} &euro;</p>
                    <p className="text-xs text-slate-400">Tot. Cliente - Costi Base</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        

      {/* Add Movimento Dialog */}
      <Dialog open={movDialogOpen} onOpenChange={setMovDialogOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader><DialogTitle>Nuovo Movimento Finanziario</DialogTitle></DialogHeader>
          <form onSubmit={handleAddMovimento} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select value={movForm.tipo} onValueChange={(v) => setMovForm({...movForm, tipo: v})}>
                <SelectTrigger data-testid="mov-tipo-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fattura_emessa">Fattura Emessa (quanto fatturato)</SelectItem>
                  <SelectItem value="pagamento_ricevuto">Pagamento Ricevuto (incasso)</SelectItem>
                  <SelectItem value="costo_extra">Costo Extra</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Importo</Label><Input type="number" step="0.01" value={movForm.importo} onChange={(e) => setMovForm({...movForm, importo: e.target.value})} required placeholder="0.00" data-testid="mov-importo-input" /></div>
              <div className="space-y-2"><Label>Data</Label><Input type="date" value={movForm.data} onChange={(e) => setMovForm({...movForm, data: e.target.value})} required data-testid="mov-data-input" /></div>
            </div>
            {movForm.tipo === 'fattura_emessa' && (
              <div className="space-y-2"><Label>Numero Fattura</Label><Input value={movForm.numero_fattura} onChange={(e) => setMovForm({...movForm, numero_fattura: e.target.value})} placeholder="FT-2026/001" data-testid="mov-num-fattura-input" /></div>
            )}
            <div className="space-y-2"><Label>Descrizione</Label><Input value={movForm.descrizione} onChange={(e) => setMovForm({...movForm, descrizione: e.target.value})} required placeholder="Descrizione del movimento" data-testid="mov-descrizione-input" /></div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setMovDialogOpen(false)}>Annulla</Button>
              <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800" data-testid="save-movimento-btn">Salva</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Spesa Dialog */}
      <Dialog open={spesaDialogOpen} onOpenChange={setSpesaDialogOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader><DialogTitle>Aggiungi Spesa Materiale</DialogTitle></DialogHeader>
          <form onSubmit={handleAddSpesa} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>N. DDT</Label><Input value={spesaForm.ddt_number} onChange={(e) => setSpesaForm({...spesaForm, ddt_number: e.target.value})} required placeholder="DDT-001" data-testid="ddt-number-input" /></div>
              <div className="space-y-2"><Label>Data</Label><Input type="date" value={spesaForm.date} onChange={(e) => setSpesaForm({...spesaForm, date: e.target.value})} required /></div>
            </div>
            <div className="space-y-2"><Label>Fornitore</Label><Input value={spesaForm.fornitore} onChange={(e) => setSpesaForm({...spesaForm, fornitore: e.target.value})} required placeholder="Nome fornitore" /></div>
            <div className="space-y-2"><Label>Descrizione</Label><Input value={spesaForm.description} onChange={(e) => setSpesaForm({...spesaForm, description: e.target.value})} required placeholder="Materiale" /></div>
            <div className="space-y-2"><Label>Costo Netto</Label><Input type="number" step="0.01" value={spesaForm.costo_netto} onChange={(e) => setSpesaForm({...spesaForm, costo_netto: e.target.value})} required placeholder="0.00" /></div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setSpesaDialogOpen(false)}>Annulla</Button>
              <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800" data-testid="save-spesa-btn">Salva</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Costo Dialog */}
      <Dialog open={costoDialogOpen} onOpenChange={setCostoDialogOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader><DialogTitle>Aggiungi Costo Operaio</DialogTitle></DialogHeader>
          <form onSubmit={handleAddCosto} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Operaio</Label>
              <Select value={costoForm.worker_id} onValueChange={selectWorker}>
                <SelectTrigger data-testid="select-worker"><SelectValue placeholder="Seleziona operaio" /></SelectTrigger>
                <SelectContent>{workers.map(w => <SelectItem key={w.id} value={w.id}>{w.name} {w.hourly_cost ? `(${w.hourly_cost}/h)` : ''}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Ore</Label><Input type="number" step="0.5" value={costoForm.hours} onChange={(e) => setCostoForm({...costoForm, hours: e.target.value})} required placeholder="8" /></div>
              <div className="space-y-2"><Label>Costo/Ora</Label><Input type="number" step="0.01" value={costoForm.hourly_cost} onChange={(e) => setCostoForm({...costoForm, hourly_cost: e.target.value})} required placeholder="25.00" /></div>
            </div>
            <div className="space-y-2"><Label>Data</Label><Input type="date" value={costoForm.date} onChange={(e) => setCostoForm({...costoForm, date: e.target.value})} required /></div>
            {costoForm.hours && costoForm.hourly_cost && (
              <div className="p-3 bg-slate-50 rounded-lg border text-center"><p className="text-sm text-slate-500">Totale: <span className="font-bold text-slate-900 text-lg">{(parseFloat(costoForm.hours) * parseFloat(costoForm.hourly_cost)).toLocaleString('it-IT', {minimumFractionDigits: 2})}</span></p></div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setCostoDialogOpen(false)}>Annulla</Button>
              <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800" data-testid="save-costo-btn">Salva</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Ricarico Dialog */}
      <Dialog open={ricaricDialogOpen} onOpenChange={setRicaricoDialogOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Imposta ricarichi cantiere</DialogTitle>
            <DialogDescription>Percentuali e importi fissi applicati ai costi base. Il ricarico fisso ora è distinto tra materiali e manodopera.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveRicarico} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Ricarico Materiali (%)</Label><Input type="text" inputMode="decimal" value={ricaricoForm.ricarico_materiali_pct} onChange={(e) => setRicaricoForm({...ricaricoForm, ricarico_materiali_pct: e.target.value.replace(/[^\d.,-]/g, '')})} placeholder="0" data-testid="ric-mat-pct" /></div>
              <div className="space-y-2"><Label>Ricarico Manodopera (%)</Label><Input type="text" inputMode="decimal" value={ricaricoForm.ricarico_manodopera_pct} onChange={(e) => setRicaricoForm({...ricaricoForm, ricarico_manodopera_pct: e.target.value.replace(/[^\d.,-]/g, '')})} placeholder="0" data-testid="ric-man-pct" /></div>
            </div>
            <div className="border-t pt-3 space-y-2">
              <Label className="text-sm font-semibold text-slate-700">Ricarico fisso (in euro)</Label>
              <p className="text-[11px] text-slate-500">Si aggiunge SEMPRE alle percentuali sopra. Distinto tra materiali e manodopera.</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Su Materiali</Label>
                  <Input type="text" inputMode="decimal" value={ricaricoForm.ricarico_fisso_materiali_eur} onChange={(e) => setRicaricoForm({...ricaricoForm, ricarico_fisso_materiali_eur: e.target.value.replace(/[^\d.,-]/g, '')})} placeholder="0,00 €" data-testid="ric-fisso-mat" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Su Manodopera</Label>
                  <Input type="text" inputMode="decimal" value={ricaricoForm.ricarico_fisso_manodopera_eur} onChange={(e) => setRicaricoForm({...ricaricoForm, ricarico_fisso_manodopera_eur: e.target.value.replace(/[^\d.,-]/g, '')})} placeholder="0,00 €" data-testid="ric-fisso-man" />
                </div>
              </div>
              <p className="text-[10px] text-slate-400">
                Totale fisso applicato: <span className="font-mono font-medium text-slate-700">
                  {(parseItNum(ricaricoForm.ricarico_fisso_materiali_eur) + parseItNum(ricaricoForm.ricarico_fisso_manodopera_eur)).toLocaleString('it-IT', {minimumFractionDigits: 2})} €
                </span>
              </p>
            </div>

            {/* iter62/63: Anteprima totale cliente LIVE — aggiornata mentre digiti */}
            {(() => {
              const totMat = riepilogo?.totale_materiali || 0;
              // iter63: usa totale_manodopera_cliente (importi già concordati)
              // per allineare l'anteprima al calcolo backend
              const totMan = (riepilogo?.totale_manodopera_cliente ?? riepilogo?.totale_manodopera) || 0;
              const totManInterno = riepilogo?.totale_manodopera || 0;
              const ricMatPct = parseItNum(ricaricoForm.ricarico_materiali_pct);
              const ricManPct = parseItNum(ricaricoForm.ricarico_manodopera_pct);
              const ricFissoMat = parseItNum(ricaricoForm.ricarico_fisso_materiali_eur);
              const ricFissoMan = parseItNum(ricaricoForm.ricarico_fisso_manodopera_eur);
              const matCliente = totMat * (1 + ricMatPct / 100) + ricFissoMat;
              // Ricarico % manodopera si calcola sul costo interno (totale_manodopera)
              // ma si aggiunge al valore-cliente concordato.
              const ricManEur = totManInterno * ricManPct / 100;
              const manCliente = totMan + ricManEur + ricFissoMan;
              const totCliente = matCliente + manCliente;
              return (
                <div className="border-t border-emerald-200 pt-3 bg-emerald-50 -mx-6 px-6 pb-3">
                  <p className="text-xs font-semibold text-emerald-800 mb-2">Anteprima Totale al Cliente (live)</p>
                  <div className="space-y-1 text-xs font-mono">
                    <div className="flex justify-between text-slate-600">
                      <span>Materiali netti</span>
                      <span>{totMat.toLocaleString('it-IT', {minimumFractionDigits: 2})} €</span>
                    </div>
                    <div className="flex justify-between text-slate-600">
                      <span>+ Ricarico mat ({ricMatPct}% + {ricFissoMat.toFixed(2)}€ fisso)</span>
                      <span className="text-emerald-700">= {matCliente.toLocaleString('it-IT', {minimumFractionDigits: 2})} €</span>
                    </div>
                    <div className="flex justify-between text-slate-600 pt-1">
                      <span>Manodopera cliente</span>
                      <span>{totMan.toLocaleString('it-IT', {minimumFractionDigits: 2})} €</span>
                    </div>
                    <div className="flex justify-between text-slate-600">
                      <span>+ Ricarico man ({ricManPct}% + {ricFissoMan.toFixed(2)}€ fisso)</span>
                      <span className="text-emerald-700">= {manCliente.toLocaleString('it-IT', {minimumFractionDigits: 2})} €</span>
                    </div>
                    <div className="flex justify-between border-t border-emerald-300 pt-2 mt-2 font-bold text-emerald-900">
                      <span>TOTALE CLIENTE</span>
                      <span data-testid="anteprima-totale-cliente" className="text-base">{totCliente.toLocaleString('it-IT', {minimumFractionDigits: 2})} €</span>
                    </div>
                  </div>
                </div>
              );
            })()}

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setRicaricoDialogOpen(false)}>Annulla</Button>
              <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800" data-testid="save-ricarico-btn">Salva</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Costo Operaio Dialog — split between internal cost / client billing */}
      <Dialog open={editCostoDialogOpen} onOpenChange={setEditCostoDialogOpen}>
        <DialogContent className="max-w-xl" data-testid="edit-costo-dialog">
          <DialogHeader>
            <DialogTitle>Modifica ore — {editingCosto?.worker_name}</DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              Le ore originali del rapportino NON vengono modificate. Qui imposti solo le ore usate per il calcolo contabile.
              {(() => {
                const w = workers.find(x => x.id === editingCosto?.worker_id);
                if (!w || (!w.hourly_cost && !w.prezzo_cliente)) return null;
                return (
                  <span className="block mt-1 text-slate-600">
                    Anagrafica operaio: <strong className="text-slate-900">{w.hourly_cost ? `${w.hourly_cost}€/h costo` : 'costo non impostato'}</strong>
                    {' · '}
                    <strong className="text-emerald-700">{w.prezzo_cliente ? `${w.prezzo_cliente}€/h cliente` : 'prezzo cliente non impostato'}</strong>
                  </span>
                );
              })()}
            </p>
          </DialogHeader>
          <form onSubmit={handleUpdateCosto} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3 rounded-lg border border-slate-200 p-3 bg-slate-50/40">
                <Label className="text-sm font-semibold text-slate-700">Costo società</Label>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Ore (costo)</Label>
                  <Input type="number" step="0.5" min="0" value={editCostoForm.hours_costo}
                    onChange={(e) => setEditCostoForm({...editCostoForm, hours_costo: e.target.value})}
                    data-testid="edit-costo-hours-costo" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">€/h interno</Label>
                  <Input type="number" step="0.01" min="0" value={editCostoForm.hourly_cost}
                    onChange={(e) => setEditCostoForm({...editCostoForm, hourly_cost: e.target.value})}
                    data-testid="edit-costo-hourly-cost" />
                </div>
                <div className="pt-2 border-t border-slate-200">
                  <p className="text-xs text-slate-500">Totale costo</p>
                  <p className="text-lg font-bold text-slate-900">
                    € {((parseFloat(editCostoForm.hours_costo) || 0) * (parseFloat(editCostoForm.hourly_cost) || 0)).toLocaleString('it-IT', {minimumFractionDigits: 2})}
                  </p>
                </div>
              </div>
              <div className="space-y-3 rounded-lg border border-emerald-200 p-3 bg-emerald-50/30">
                <Label className="text-sm font-semibold text-emerald-700">Prezzo cliente</Label>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Ore (fatturate)</Label>
                  <Input type="number" step="0.5" min="0" value={editCostoForm.hours_cliente}
                    onChange={(e) => setEditCostoForm({...editCostoForm, hours_cliente: e.target.value})}
                    data-testid="edit-costo-hours-cliente" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">€/h cliente</Label>
                  <Input type="number" step="0.01" min="0" value={editCostoForm.prezzo_cliente}
                    onChange={(e) => setEditCostoForm({...editCostoForm, prezzo_cliente: e.target.value})}
                    data-testid="edit-costo-prezzo-cliente" />
                </div>
                <div className="pt-2 border-t border-emerald-200">
                  <p className="text-xs text-slate-500">Totale fatturato</p>
                  <p className="text-lg font-bold text-emerald-700">
                    € {((parseFloat(editCostoForm.hours_cliente) || 0) * (parseFloat(editCostoForm.prezzo_cliente) || 0)).toLocaleString('it-IT', {minimumFractionDigits: 2})}
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-md bg-slate-900 px-3 py-2 text-white text-sm flex items-center justify-between">
              <span>Margine</span>
              <span className="font-bold">
                € {(
                  ((parseFloat(editCostoForm.hours_cliente) || 0) * (parseFloat(editCostoForm.prezzo_cliente) || 0))
                  - ((parseFloat(editCostoForm.hours_costo) || 0) * (parseFloat(editCostoForm.hourly_cost) || 0))
                ).toLocaleString('it-IT', {minimumFractionDigits: 2})}
              </span>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setEditCostoDialogOpen(false)}>Annulla</Button>
              <Button type="submit" className="bg-slate-900 text-white" data-testid="submit-edit-costo">Salva</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Scarica Conto AI */}
      <Dialog open={contoAiDialogOpen} onOpenChange={setContoAiDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Wand2 className="h-5 w-5 text-purple-600" />Genera Conto Cantiere con AI</DialogTitle>
            <DialogDescription>
              L'AI compone automaticamente il testo del conto leggendo materiali, ore, lavorazioni e costi del cantiere.
              Il documento viene poi inserito nel modello Word scelto e scaricato pronto da inviare al cliente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Modello Word da usare</Label>
              <Select value={contoAiForm.template_id || 'default'} onValueChange={(v) => setContoAiForm({...contoAiForm, template_id: v === 'default' ? '' : v})}>
                <SelectTrigger data-testid="conto-ai-template"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Layout predefinito (consigliato)</SelectItem>
                  {contoTemplates.map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name} ({t.doc_type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                {contoTemplates.length === 0
                  ? 'Nessun modello caricato — viene usato il layout default. Per personalizzare vai in Impostazioni → Modelli documenti.'
                  : 'Il modello scelto deve contenere placeholder Jinja (es. {{ cliente_nome }}, {{ totale }}, {{ testo_intro }}, ecc.)'}
              </p>
            </div>
            <div>
              <Label>Formato file</Label>
              <Select value={contoAiForm.output} onValueChange={(v) => setContoAiForm({...contoAiForm, output: v})}>
                <SelectTrigger data-testid="conto-ai-format"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="docx">Word (.docx) — modificabile</SelectItem>
                  <SelectItem value="pdf">PDF — pronto per invio</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Istruzioni aggiuntive per l'AI (opzionale)</Label>
              <Textarea
                rows={3}
                value={contoAiForm.extra_instructions}
                onChange={(e) => setContoAiForm({...contoAiForm, extra_instructions: e.target.value})}
                placeholder="es. enfatizza le difficoltà tecniche, includi sconto di cortesia, ringrazia per la collaborazione…"
                data-testid="conto-ai-instructions"
              />
            </div>
            <div className="rounded-md bg-purple-50 border border-purple-200 p-3 text-xs text-purple-900">
              <p className="font-semibold mb-1">L'AI scriverà:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Intro al cliente con riferimento al cantiere e al periodo lavori</li>
                <li>Descrizione narrativa delle lavorazioni svolte (basata sui rapportini)</li>
                <li>Oggetto del documento</li>
                <li>Chiusura con condizioni di pagamento</li>
              </ul>
              <p className="mt-2 italic">Tabelle materiali, ore e totali vengono compilate automaticamente con i dati reali del cantiere.</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setContoAiDialogOpen(false)} disabled={contoAiBusy}>Annulla</Button>
              <Button onClick={handleGenerateContoAi} disabled={contoAiBusy} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="conto-ai-generate">
                <Wand2 className={`h-4 w-4 mr-2 ${contoAiBusy ? 'animate-pulse' : ''}`} />
                {contoAiBusy ? 'Genero…' : 'Genera e scarica'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog: Anteprima Conto AI (lettura inline, niente download) */}
      <Dialog open={contoPreviewOpen} onOpenChange={setContoPreviewOpen}>
        <DialogContent className="sm:max-w-[820px] max-h-[90vh] overflow-y-auto" data-testid="conto-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-indigo-600" />
              Anteprima Conto AI — {cantiereCorrente?.name || ''}
            </DialogTitle>
            <DialogDescription>
              Anteprima del rendiconto generato dall'AI. Le lavorazioni sono divise in punti chiave per una lettura rapida.
            </DialogDescription>
          </DialogHeader>

          {contoPreviewBusy && (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500" data-testid="conto-preview-loading">
              <Sparkles className="h-8 w-8 animate-pulse text-indigo-500 mb-2" />
              <p className="text-sm">L'AI sta componendo il conto…</p>
              <p className="text-xs text-slate-400 mt-1">Attendi 5-15 secondi</p>
            </div>
          )}

          {!contoPreviewBusy && contoPreviewData && (
            <div className="space-y-5" data-testid="conto-preview-content">
              {/* Oggetto */}
              <div className="bg-indigo-50/60 border border-indigo-200 rounded-lg p-4">
                <p className="text-[10px] uppercase font-semibold text-indigo-700 tracking-wider mb-1">Oggetto</p>
                <p className="text-base font-semibold text-slate-900" data-testid="preview-oggetto">{contoPreviewData.oggetto || '-'}</p>
              </div>

              {/* Cliente + data */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-[10px] uppercase font-semibold text-slate-500 tracking-wider mb-1">Cliente</p>
                  <p className="font-semibold text-slate-900">{contoPreviewData.cliente_nome || '-'}</p>
                  {contoPreviewData.cliente_indirizzo && <p className="text-slate-600 text-xs">{contoPreviewData.cliente_indirizzo}</p>}
                  {contoPreviewData.cliente_piva && <p className="text-slate-600 text-xs font-mono">P.IVA {contoPreviewData.cliente_piva}</p>}
                </div>
                <div>
                  <p className="text-[10px] uppercase font-semibold text-slate-500 tracking-wider mb-1">Periodo lavori</p>
                  <p className="font-semibold text-slate-900 text-xs">
                    Dal <span className="font-mono">{contoPreviewData.data_inizio || '-'}</span> al <span className="font-mono">{contoPreviewData.data_fine || '-'}</span>
                  </p>
                  <p className="text-slate-600 text-xs">Documento: <span className="font-mono">{contoPreviewData.numero || '-'}</span></p>
                  <p className="text-slate-600 text-xs">Data: <span className="font-mono">{contoPreviewData.data || '-'}</span></p>
                </div>
              </div>

              {/* Intro */}
              {contoPreviewData.testo_intro && (
                <div className="text-sm text-slate-700 italic leading-relaxed border-l-4 border-indigo-200 pl-3" data-testid="preview-intro">
                  {contoPreviewData.testo_intro}
                </div>
              )}

              {/* PUNTI LAVORAZIONI - ELENCO NUMERATO (la richiesta principale) */}
              <div>
                <h3 className="text-sm font-bold text-slate-900 mb-2 uppercase tracking-wide flex items-center gap-2">
                  <FileText className="h-4 w-4 text-indigo-600" />
                  Lavorazioni svolte (riassunto)
                </h3>
                {Array.isArray(contoPreviewData.lavorazioni_punti) && contoPreviewData.lavorazioni_punti.length > 0 ? (
                  <ol className="list-decimal list-outside ml-5 space-y-1.5 text-sm text-slate-800" data-testid="preview-lavorazioni-list">
                    {contoPreviewData.lavorazioni_punti.map((punto, idx) => (
                      <li key={`punto-${idx}`} className="pl-1" data-testid={`preview-punto-${idx}`}>
                        {punto}
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p className="text-xs text-slate-500 italic">Nessun punto generato — verifica i rapportini del cantiere.</p>
                )}

                {/* Paragrafo descrittivo (in coda all'elenco, come contesto) */}
                {contoPreviewData.testo_lavorazioni && (
                  <details className="mt-3 text-xs text-slate-600 bg-slate-50 rounded p-2">
                    <summary className="cursor-pointer font-medium text-slate-700">Mostra descrizione narrativa</summary>
                    <p className="mt-2 italic leading-relaxed">{contoPreviewData.testo_lavorazioni}</p>
                  </details>
                )}
              </div>

              {/* Riepilogo economico */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <h3 className="text-sm font-bold text-slate-900 mb-3 uppercase tracking-wide flex items-center gap-2">
                  <Euro className="h-4 w-4 text-emerald-600" />
                  Riepilogo economico
                </h3>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Materiali ({contoPreviewData.n_materiali || 0} voci)</span>
                    <span className="font-mono font-medium text-slate-900">€ {(contoPreviewData.totale_materiali || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Manodopera ({contoPreviewData.n_costi || 0} righe)</span>
                    <span className="font-mono font-medium text-slate-900">€ {(contoPreviewData.totale_manodopera_cliente || 0).toFixed(2)}</span>
                  </div>
                  {contoPreviewData.ricarico_materiali_eur > 0 && (
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>Ricarico materiali ({contoPreviewData.ricarico_materiali_pct}%)</span>
                      <span className="font-mono">€ {contoPreviewData.ricarico_materiali_eur.toFixed(2)}</span>
                    </div>
                  )}
                  {contoPreviewData.ricarico_fisso_eur > 0 && (
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>Ricarico fisso</span>
                      <span className="font-mono">€ {contoPreviewData.ricarico_fisso_eur.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-slate-300 pt-2 mt-2">
                    <span className="font-bold text-slate-900">TOTALE AL CLIENTE</span>
                    <span className="font-mono font-bold text-emerald-700 text-lg" data-testid="preview-totale">€ {(contoPreviewData.totale || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-slate-500">
                    <span>Margine stimato</span>
                    <span className="font-mono">€ {(contoPreviewData.margine || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Chiusura */}
              {contoPreviewData.testo_chiusura && (
                <div className="text-sm text-slate-700 italic leading-relaxed border-l-4 border-emerald-200 pl-3" data-testid="preview-chiusura">
                  {contoPreviewData.testo_chiusura}
                </div>
              )}

              {/* Re-genera con istruzioni */}
              <div className="border-t pt-4 space-y-2">
                <Label className="text-xs">Affina con istruzioni aggiuntive (opzionale)</Label>
                <Textarea
                  rows={2}
                  value={contoPreviewExtra}
                  onChange={(e) => setContoPreviewExtra(e.target.value)}
                  placeholder="es. tono più colloquiale, enfatizza la cura del cliente, includi sconto…"
                  className="text-sm"
                  data-testid="preview-extra-instructions"
                />
                <div className="flex justify-between items-center">
                  <p className="text-[11px] text-slate-400 italic">
                    Non viene scaricato nulla — questa è solo lettura. Per scaricare usa "Scarica Conto AI".
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => fetchContoPreview(contoPreviewExtra)}
                    disabled={contoPreviewBusy}
                    data-testid="preview-regenerate"
                  >
                    <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${contoPreviewBusy ? 'animate-spin' : ''}`} />
                    Rigenera
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: Crea / Modifica Conto Emesso (iter39) */}
      <Dialog open={contoEmessoDialogOpen} onOpenChange={setContoEmessoDialogOpen}>
        <DialogContent className="sm:max-w-[560px]" data-testid="conto-emesso-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSignature className="h-5 w-5 text-emerald-600" />
              {editingContoEmesso ? 'Modifica conto emesso' : 'Genera nuovo conto'}
            </DialogTitle>
            <DialogDescription>
              Il <b>prezzo calcolato</b> è quello matematico del riepilogo. Il <b>prezzo emesso</b> è quello effettivo che invii al cliente (puoi arrotondarlo).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveContoEmesso} className="space-y-3 mt-2">
            <div className="space-y-1">
              <Label className="text-xs">Descrizione</Label>
              <Input value={contoEmessoForm.descrizione} onChange={(e) => setContoEmessoForm({...contoEmessoForm, descrizione: e.target.value})} placeholder="es. Conto lavori impianto cliente X" data-testid="ce-descrizione" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Data emissione</Label>
                <Input type="date" value={contoEmessoForm.data_emissione} onChange={(e) => setContoEmessoForm({...contoEmessoForm, data_emissione: e.target.value})} data-testid="ce-data" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Stato</Label>
                <Select value={contoEmessoForm.stato} onValueChange={(v) => setContoEmessoForm({...contoEmessoForm, stato: v})}>
                  <SelectTrigger data-testid="ce-stato"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bozza">Bozza</SelectItem>
                    <SelectItem value="inviato">Inviato</SelectItem>
                    <SelectItem value="pagato">Pagato</SelectItem>
                    <SelectItem value="annullato">Annullato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1">
                  <Calculator className="h-3 w-3 text-slate-400" />
                  Prezzo calcolato (€)
                </Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={contoEmessoForm.prezzo_calcolato}
                  onChange={(e) => setContoEmessoForm({...contoEmessoForm, prezzo_calcolato: e.target.value.replace(/[^\d.,-]/g, '')})}
                  className="bg-slate-50 text-slate-600 font-mono"
                  data-testid="ce-prezzo-calcolato"
                  placeholder="0,00"
                />
                <p className="text-[10px] text-slate-400">Dalla contabilità matematica</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1">
                  <Euro className="h-3 w-3 text-emerald-600" />
                  Prezzo emesso al cliente (€) *
                </Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={contoEmessoForm.prezzo_emesso}
                  onChange={(e) => setContoEmessoForm({...contoEmessoForm, prezzo_emesso: e.target.value.replace(/[^\d.,-]/g, '')})}
                  className="bg-emerald-50 border-emerald-300 font-mono font-bold text-emerald-900"
                  required
                  data-testid="ce-prezzo-emesso"
                  placeholder="0,00"
                />
                <p className="text-[10px] text-slate-400">Modificabile (es. arrotondamento). Puoi scrivere con virgola o punto.</p>
              </div>
            </div>
            {(parseItNum(contoEmessoForm.prezzo_emesso)) !== (parseItNum(contoEmessoForm.prezzo_calcolato)) && (
              <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-800">
                Differenza vs calcolato: <span className="font-mono font-bold">
                  {(parseItNum(contoEmessoForm.prezzo_emesso) - parseItNum(contoEmessoForm.prezzo_calcolato)).toLocaleString('it-IT', {minimumFractionDigits: 2})} €
                </span>
              </div>
            )}

            {/* iter40: scelta IVA cliente */}
            <div className="border-t pt-3 space-y-2 bg-blue-50/40 rounded-lg p-3 -mx-1">
              <Label className="text-sm font-semibold text-blue-900">IVA cliente</Label>
              <p className="text-[11px] text-blue-700">
                Scegli SE applicare l'IVA al cliente finale e quale aliquota. Lascia 0% se il documento è esente / split payment / fuori IVA.
              </p>
              <div className="flex flex-wrap gap-2">
                {[
                  { v: 0, label: 'Nessuna' },
                  { v: 4, label: '4%' },
                  { v: 10, label: '10%' },
                  { v: 22, label: '22%' },
                ].map(opt => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setContoEmessoForm({...contoEmessoForm, iva_pct: opt.v, iva_custom: false})}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      !contoEmessoForm.iva_custom && parseFloat(contoEmessoForm.iva_pct) === opt.v
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-slate-700 border-slate-300 hover:border-blue-400'
                    }`}
                    data-testid={`ce-iva-${opt.v}`}
                  >
                    {opt.label}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setContoEmessoForm({...contoEmessoForm, iva_custom: true})}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    contoEmessoForm.iva_custom
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-slate-700 border-slate-300 hover:border-blue-400'
                  }`}
                  data-testid="ce-iva-custom"
                >
                  Custom
                </button>
                {contoEmessoForm.iva_custom && (
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={contoEmessoForm.iva_pct}
                    onChange={(e) => setContoEmessoForm({...contoEmessoForm, iva_pct: e.target.value.replace(/[^\d.,]/g, '')})}
                    className="h-8 w-24 text-xs"
                    placeholder="es. 5"
                    data-testid="ce-iva-custom-input"
                  />
                )}
              </div>
              {/* Anteprima IVA */}
              <div className="border-t border-blue-200 pt-2 space-y-1 text-xs font-mono">
                <div className="flex justify-between">
                  <span className="text-slate-600">Imponibile</span>
                  <span className="text-slate-800">{(parseItNum(contoEmessoForm.prezzo_emesso)).toLocaleString('it-IT', {minimumFractionDigits: 2})} €</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">IVA {parseItNum(contoEmessoForm.iva_pct)}%</span>
                  <span className="text-slate-800">
                    {(parseItNum(contoEmessoForm.prezzo_emesso) * parseItNum(contoEmessoForm.iva_pct) / 100).toLocaleString('it-IT', {minimumFractionDigits: 2})} €
                  </span>
                </div>
                <div className="flex justify-between border-t border-blue-300 pt-1">
                  <span className="font-bold text-blue-900">Totale lordo al cliente</span>
                  <span className="font-bold text-blue-900" data-testid="ce-totale-lordo">
                    {(parseItNum(contoEmessoForm.prezzo_emesso) * (1 + parseItNum(contoEmessoForm.iva_pct) / 100)).toLocaleString('it-IT', {minimumFractionDigits: 2})} €
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Note interne</Label>
              <Input value={contoEmessoForm.note} onChange={(e) => setContoEmessoForm({...contoEmessoForm, note: e.target.value})} placeholder="Note opzionali" />
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button type="button" variant="outline" onClick={() => setContoEmessoDialogOpen(false)}>
                <X className="h-4 w-4 mr-1.5" />Annulla
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="ce-save">
                <Save className="h-4 w-4 mr-1.5" />
                {editingContoEmesso ? 'Aggiorna' : 'Crea conto'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      {/* Convert to fattura confirm dialog (iter42) */}
      <Dialog open={convertConfirmOpen} onOpenChange={setConvertConfirmOpen}>
        <DialogContent className="sm:max-w-[480px]" data-testid="convert-fattura-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowUpRight className="h-5 w-5 text-emerald-600" />
              Trasforma in fattura attiva
            </DialogTitle>
            <DialogDescription>
              Verrà creata una fattura attiva con i dati del conto emesso e collegata automaticamente alla contabilità del cantiere.
            </DialogDescription>
          </DialogHeader>
          {contoToConvert && (
            <div className="space-y-2 text-sm bg-slate-50 rounded-lg p-3 border" data-testid="convert-fattura-summary">
              <div className="flex justify-between"><span className="text-slate-500">Cliente</span><span className="font-medium">{contoToConvert.cliente_nome || '-'}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Descrizione</span><span className="font-medium truncate max-w-[260px]">{contoToConvert.descrizione}</span></div>
              <div className="flex justify-between border-t pt-1.5"><span className="text-slate-500">Imponibile</span><span className="font-mono">{(contoToConvert.prezzo_emesso || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} €</span></div>
              <div className="flex justify-between"><span className="text-slate-500">IVA {contoToConvert.iva_pct || 0}%</span><span className="font-mono">{(contoToConvert.iva_eur || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} €</span></div>
              <div className="flex justify-between border-t pt-1.5"><span className="font-bold text-slate-800">Totale fattura</span><span className="font-mono font-bold text-emerald-700">{(contoToConvert.totale_lordo || contoToConvert.prezzo_emesso || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} €</span></div>
              <p className="text-[11px] text-slate-400 pt-1 border-t">Numero progressivo + scadenza 30gg dalla data di oggi. Il conto verrà marcato come "inviato" e collegato alla fattura.</p>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-3 border-t">
            <Button variant="outline" onClick={() => setConvertConfirmOpen(false)}>Annulla</Button>
            <Button onClick={handleConvertToFattura} disabled={convertingBusy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="convert-fattura-confirm">
              {convertingBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowUpRight className="h-4 w-4 mr-2" />}
              {convertingBusy ? 'Creazione…' : 'Crea fattura'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
